// import * as VegaLiteImport from '../vega-lite/build/vega-lite.js';
import { default as VegaLiteImport } from "../vg/vega-lite.js";
import * as d3 from 'd3';
import './default.js';
import * as Fronto from "./utility.js";

const vegaLite = VegaLiteImport;

const isDebug = false; // setting debugging mode

function lm(t) {
  return ["%c 🧩[Fronto] " + t + ": ", 'padding: 1.5px; background: #F0F4C3; color: #33691E'];
}

export async function compileFronto(spec, config = {}) {
  // Fronto compiler for Vega-Lite
  // @spec: a Fronto specification
  // returns a promise with the compiled spec
  // compiled spec includes: view (a Vega-Lite Spec), annotation, emphasis, and title.
  const base_url = config?.baseUrl || "";
  // 1. Extract the data, and get the data type information for missings.
  let dataset = spec.data;
  let datatype = (spec.datatype ? spec.datatype : {});
  let calculates = spec.transform?.filter((d) => d.calculate) || [];
  spec.layer?.forEach((layer) => {
    if (layer.transform?.constructor.name === "Array") {
      calculates.push(...layer.transform?.filter((d) => d.calculate));
    }
  })
  let dataExtract = Fronto.getDatasetInfo(dataset, calculates);
  let mark_label_numbering_count = 0;
  dataExtract.forEach((column, i) => {
    if (datatype[column.field] === undefined) datatype[column.field] = {};
    if (datatype[column.field]['type'] === undefined) datatype[column.field]['type'] = dataExtract[i].type;
    if (datatype[column.field]['entropy'] === undefined) datatype[column.field]['entropy'] = dataExtract[i].entropy;
    if (datatype[column.field]['cardinality'] === undefined) datatype[column.field]['cardinality'] = dataExtract[i].cardinality;
    if (datatype[column.field]['min'] === undefined) datatype[column.field]['min'] = dataExtract[i].min;
    if (datatype[column.field]['max'] === undefined) datatype[column.field]['max'] = dataExtract[i].max;
    datatype[column.field]['values'] = dataExtract[i].values;
  })
  spec.datatype = datatype; // TODO: determine whether it has to be included.

  if (isDebug) {// debugging auto-generated datatype
    console.log(...lm("Auto-generated datatype informtion, right after compiling it"));
    console.log(spec.datatype);
  }

  // 2. Prepare the view spec (Vega-Lite)
  let view = { // vega-lite spec, may changed to vega spec
    "$schema": "https://vega.github.io/schema/vega-lite/v5.json",
    "view": { "stroke": null },
    "datasets": { "values": dataset }
  };
  if (spec.config !== undefined) {
    view.config = spec.config
  }
  if (spec.layout?.stroke !== undefined) view.view.stroke = true;
  if (spec.layout?.strokeColor !== undefined) {
    view.view.stroke = spec.layout.strokeColor;
  }
  let annotation = {
    "data": {
      "items": []
    },
    "nondata": {
      "items": []
    }
  };
  let tooltipDetail = {
    "expressions": []
  };
  let emphasis = {
    "items": []
  };
  let textReplacer = {}; // "style selector (should be unique) : text content HTML (pre-generated!)"
  // get the view's name
  if (spec.name) view.name = spec.name;

  // 2.a Layout 
  // set sizing (if not set, use VL default)
  if (spec.layout?.width) view.width = spec.layout.width; // width
  if (spec.layout?.height) view.height = spec.layout.height; // height
  // get the type of layout
  let layout_type; // either "layered", "faceted" (split, small multiples), "tabular", "context", and "projection" (map).
  let has_context = spec.interaction?.context !== undefined;
  let has_splits = spec.layout?.split?.field !== undefined && (spec.layout?.split?.field?.length > 0 || spec.layout?.split?.filter?.length > 0);
  if (has_context) {
    layout_type = "context";
  } else if (spec.layout?.composition === "repeated" || has_splits) {
    layout_type = "faceted";
  } else if (spec.layout?.composition === "projection" || spec.projection !== undefined) {
    layout_type = "projection";
  } else {
    let n_rows = (spec.layout?.row ? spec.layout.row.length : 0);
    let n_cols = (spec.layout?.column ? spec.layout.column.length : 0);
    if (n_rows <= 1 && n_cols <= 1) layout_type = "layered";
    else layout_type = "tabular";
  }

  if (spec.layout?.padding !== undefined && spec.layout.composition === "single") {
    view.padding = Fronto.deepcopy(spec.layout.padding);
    if (view.padding.left) view.width -= view.padding.left;
    if (view.padding.right) view.width -= view.padding.right;
    if (view.padding.top) view.height -= view.padding.top;
    if (view.padding.bottom) view.height -= view.padding.bottom;
  }

  if (isDebug) {// debugging layout_type
    console.log(...lm("Layout Type, right after detection"));
    console.log(layout_type);
  }

  // setting autofit
  if (layout_type === "layered" || layout_type === "projection") {
    view.autosize = "fit";
  }
  // get the facet layout for faceted views.
  let facet_layout = {};

  let width_adjustment_for_facets = 0; // sum of width of horizontal axis/legend
  let height_adjustment_for_facets = 0; // sum of height of horizontal axis/legend
  let row_fields = (spec.layout?.row ? spec.layout.row.map(d => Fronto.get_rc_field(d)) : []);
  let col_fields = (spec.layout?.column ? spec.layout.column.map(d => Fronto.get_rc_field(d)) : []);
  if (layout_type === "faceted" || layout_type === "tabular") {
    spec.layer?.forEach((layer, li) => {
      layer.text?.forEach((textItem, ti) => {
        if (textItem.type === "on-axis") {
          if (row_fields.includes(textItem.field) && (!["top", "bottom"].includes(textItem.orient) && !textItem.serial)) {
            width_adjustment_for_facets += textItem?.items?.map(t => t.width ? t.width : 20).f$max() || 0;
          }
          if (col_fields.includes(textItem.field) && (["top", "bottom"].includes(textItem.orient) || textItem.serial)) {
            height_adjustment_for_facets += textItem?.items?.map(t => t.fontSize ? t.fontSize : 12).f$sum() || 0;
          }
        } else if (textItem.type === "legend") {
          if (textItem.orient === "left" || textItem.orient === "right") {
            width_adjustment_for_facets += textItem?.items?.map(t => t.width ? t.width : 50).f$max() || 0;
          }
          if (textItem.orient === "top" || textItem.orient === "bottom") {
            width_adjustment_for_facets += textItem?.items?.map(t => t.fontSize ? t.fontSize : 12).f$sum() || 0;
          }
        }
      })
    });
    width_adjustment_for_facets += 20 * ((spec.layout?.nColumns || 1) - 1);
    let n_facets;
    n_facets = spec.layout?.split?.values || spec.datatype?.[spec.layout?.split?.field]?.cardinality || 0;
    let n_facet_rows = Math.ceil(n_facets / (spec.layout?.nColumns || 1));
    height_adjustment_for_facets += 20 * ((n_facet_rows || 1) - 1);
  }

  let ft_adjusted_size = [spec.layout?.width - width_adjustment_for_facets, spec.layout?.height - height_adjustment_for_facets];
  if (isDebug) {// debugging ft_adjusted_size, width_adjustment_for_facets, height_adjustment_for_facets
    console.log(...lm("Size adjustment, right after the detection; adjusted size, width adjustment for facets, height adjustment for facets"));
    console.log(ft_adjusted_size, width_adjustment_for_facets, height_adjustment_for_facets);
  }

  if (layout_type === "faceted") {
    // get the number of facets
    facet_layout.n_facets = 1;
    if (spec.layout?.split?.field !== undefined) {
      // small multiples: if `values` are specified then the number of values, otherwise the cardinality of the field;
      facet_layout.n_facets = (spec.layout.split.values ? spec.layout.split.values.length : datatype[spec.layout.split.field].cardinality);
    } else if (spec.layout?.split?.filter !== undefined) {
      // custom split views: the number of filters
      facet_layout.n_facets = spec.layout.split.filter.length;
    }
    // compute facet sizing info
    facet_layout.sizing_option = (["entire", "facet"].includes(spec.layout?.facetSizing) ? spec.layout.facetSizing : "entire");
    facet_layout.n_columns = (spec.layout?.nColumns ? spec.layout.nColumns : 1);
    facet_layout.n_rows = Math.ceil(facet_layout.n_facets / facet_layout.n_columns);
    if (spec.layout?.width) facet_layout.width = (ft_adjusted_size[0] - 5) / facet_layout.n_columns - 5;
    if (spec.layout?.height) facet_layout.height = (ft_adjusted_size[1] - 5) / facet_layout.n_rows - 5;
  } else if (layout_type === "tabular") {
    // get the number of rows/columns
    let col_name = (spec.layout.column?.[0]?.constructor.name === "Array" ? spec.layout.column[0][0] : (spec.layout.column?.[0]?.field ? spec.layout.column?.[0].field : spec.layout.column?.[0])) || undefined;
    let row_name = (spec.layout.row?.[0]?.constructor.name === "Array" ? spec.layout.row[0][0] : (spec.layout.row?.[0]?.field ? spec.layout.row?.[0].field : spec.layout.row?.[0])) || undefined;
    facet_layout.n_columns = spec.layout.column?.length > 1 ? datatype[col_name]?.cardinality || 3 : 1;
    facet_layout.n_rows = spec.layout.row?.length > 1 ? datatype[row_name]?.cardinality || 3 : 1;
    if (spec.layout?.width) {
      if (facet_layout.n_columns > 1) facet_layout.width = Math.round((ft_adjusted_size[0] - 5) / facet_layout.n_columns) + 5;
      else facet_layout.width = ft_adjusted_size[0];
    }
    if (facet_layout.width < 0) facet_layout.width = 15;
    if (spec.layout?.height) {
      if (facet_layout.n_rows > 1) facet_layout.height = Math.round((ft_adjusted_size[1] - 15) / facet_layout.n_rows) - 15;
      else facet_layout.height = ft_adjusted_size[1];
    }
    if (facet_layout.width < 0) facet_layout.height = 15
  }
  // update sizing
  if (facet_layout.width) view.width = facet_layout.width;
  if (facet_layout.height) view.height = facet_layout.height;

  if (isDebug) {// debugging facet_layout
    console.log(...lm("Facet layout, right after compiling"));
    console.log(facet_layout);
  }

  // 2.a+ set projectcion
  if (layout_type === "projection") {
    view.projection = {};
    if (spec.layout.projection?.scale) view.projection.scale = spec.layout.projection.scale;
    if (spec.layout.projection?.translate) view.projection.translate = spec.layout.projection.translate;
    if (spec.layout.projection?.center) view.projection.center = [spec.layout.projection.center[0], spec.layout.projection.center[1]] || [0, 0];
    if (spec.layout.projection?.type) view.projection.type = spec.layout.projection.type;
    else view.projection.type = (['us-state', 'us-county'].includes(spec.layout.projection?.level) ? "albersUSA" : "mercator");
    if (spec.layout.projection?.transpose === true) view.projection.rotate = [0, 0, 90];
    if (spec.layout.projection?.repeat) {
      view.projection.clipAngle = 0;
      view.projection.rotate = [view.projection.center[0], 0, 0];
      view.projection.center[0] = 0;
    }
    // transverseMercator
    // if not specified, default as albersUSA/mercator
  }

  // 2.b Get global transformations.
  if (spec.transform !== undefined && spec.transform.constructor.name === "Array")
    view.transform = Fronto.deepcopy(spec.transform) // to prevent further changes.

  // 3. Format layers (mark, encoding, scales, axis, legend)
  // 3.a get the number of layers
  let n_layers = (spec.layer?.length ? spec.layer.length : 0);
  if (!["layered", "projection"].includes(layout_type) && n_layers > 1) n_layers = 1;
  // 3.b for each layer ...
  let layers = Fronto.range(n_layers).map(i => {
    let layer_info = spec.layer[i];
    let params = []; // parameters for interaction
    // 3.b1 name and data (done)
    let name = "F_layer_" + i + (layer_info.name ? "__named_" + layer_info.name : ""); // name
    let data = { // data (done)
      "name": "values"
    };
    // 3.b2 mark & encoding
    let mark = {};// mark
    if (layer_info.mark?.type) mark.type = layer_info.mark.type;
    if (layer_info.mark?.interpolate) mark.interpolate = layer_info.mark.interpolate;
    let encoding = {}; // encoding
    // 3.b3 mark properties 
    if (layer_info.width) mark.width = layer_info.width;
    if (layer_info.height) mark.height = layer_info.height;
    if (layer_info.mark?.feature) mark.feature = layer_info.mark.feature;
    // position encodings
    let enc_x, enc_col, enc_y, enc_row, enc_long, enc_lat;
    if (layout_type === "projection") { // projection
      enc_long = spec.layout?.column?.[0];
    } else if (spec.layout?.column?.length == 2) { // column, x
      enc_x = spec.layout.column[1];
      enc_col = spec.layout.column[0];
    } else if (spec.layout?.column?.length == 1) {
      enc_x = spec.layout.column[0];
    }
    if (layout_type === "projection") { // projection longitude
      enc_lat = spec.layout?.row?.[0];
    } else if (spec.layout?.row?.length == 2) { // row, y
      enc_y = spec.layout.row[1];
      enc_row = spec.layout.row[0];
    } else if (spec.layout?.row?.length == 1) {
      enc_y = spec.layout.row[0];
    }

    let layer_enc_x, layer_enc_col, layer_enc_y, layer_enc_row, layer_enc_long, layer_enc_lat;
    if (layer_info.column !== undefined) {
      if (layout_type === "projection") { // projection
        layer_enc_long = layer_info.column[0];
      } else if (layer_info.column?.length == 2) { // column, x
        layer_enc_x = layer_info.column[1];
        layer_enc_col = layer_info.column[0];
      } else if (layer_info.column?.length == 1) {
        layer_enc_x = layer_info.column[0];
      }
    }
    if (layer_info.row !== undefined) {
      if (layout_type === "projection") { // projection longitude
        layer_enc_lat = layer_info.row[0];
      } else if (layer_info.row?.length == 2) { // row, y
        layer_enc_y = layer_info.row[1];
        layer_enc_row = layer_info.row[0];
      } else if (layer_info.row?.length == 1) {
        layer_enc_y = layer_info.row[0];
      }
    }

    if (enc_long) {
      encoding.longitude = {};
      if (enc_long.constructor.name === "String") {
        encoding.longitude.field = enc_long;
        encoding.longitude.type = "quantitative";
      } else if (enc_long.constructor.name === "Object") {
        encoding.longitude.field = enc_long.field;
        encoding.longitude.type = "quantitative";
      }
      if (layer_enc_long !== undefined) {
        Object.keys(layer_enc_long).forEach(key => {
          encoding.longitude[key] = layer_enc_long[key];
        })
      }
    }

    if (enc_lat) {
      encoding.latitude = {};
      if (enc_lat.constructor.name === "String") {
        encoding.latitude.field = enc_lat;
        encoding.latitude.type = "quantitative";
      } else if (enc_long.constructor.name === "Object") {
        encoding.latitude.field = enc_lat.field;
        encoding.latitude.type = "quantitative";
      }
      if (layer_enc_lat !== undefined) {
        Object.keys(layer_enc_lat).forEach(key => {
          encoding.latitude[key] = layer_enc_lat[key];
        })
      }
    }
    if (enc_col) {
      // define column encoding
      encoding.column = {};
      if (enc_col.constructor.name === "String") { // given as a field name
        encoding.column.field = enc_col;
        encoding.column.type = datatype[enc_col]?.type;
      } else { // given as an object
        encoding.column.field = enc_col.field;
        encoding.column.type = (enc_col.type !== undefined ? enc_col.type : datatype[enc_col.field]?.type);
        if (enc_col.sort == null) {
          encoding.column.sort = null;
        } else if (enc_col.sort !== undefined) { // sorting
          if (enc_col.sort.constructor.name === "String") { //by a field
            encoding.column.sort = enc_col.sort;
          } else if (enc_col.sort.constructor.name === "Object") { // more details
            if (enc_col.sort.field !== undefined && enc_col.sort.order?.constructor.name === "String") { // by a field
              encoding.column.sort = { "descending": "-", "ascending": "" }[enc_col.sort.order] + "datum." + enc_col.sort.field;
            } else if (enc_col.sort.field !== undefined && enc_col.sort.order === undefined) { // no particular order is provided
              encoding.column.sort = "datum." + enc_col.sort.field;
            } else if (enc_col.sort.order?.constructor.name === "Array") { // particular order is provided
              encoding.column.sort = enc_col.sort.order;
            }
          }
          if (enc_col.sort.reverse !== undefined && enc_col.sort.reverse == true) { // if reversed
            if (encoding.column.sort?.constructor.name === "Array") encoding.column.sort = encoding.column.sort.reverse();
          }
          if (enc_col.sort.order) { // register the sort order in the datatype object
            datatype[enc_col.field].order = (enc_col.sort.reverse == true ? enc_col.sort.order.reverse() : enc_col.sort.order);
            if (enc_col.sort.field !== undefined && enc_col.sort.order === "ascending") {
              datatype[enc_col.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[enc_col.sort.field] - b[enc_col.sort.field]; }).map(d => d[enc_col.field]);
            } else if (enc_col.sort.field !== undefined && enc_col.sort.order === "descending") {
              datatype[enc_col.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[enc_col.sort.field] - a[enc_col.sort.field]; }).map(d => d[enc_col.field]);
            }
          }
        }
      }
      if (enc_col.aggregate) {
        encoding.column.aggregate = enc_col.aggregate;
      }
      if (enc_col.bin) {
        encoding.column.bin = enc_col.bin;
      }
      // axis design --> only text (when dealing text)
      encoding.column.header = {};
      encoding.column.header.title = null;
      encoding.column.header.labels = false;

      // layer specific
      if (layer_enc_col !== undefined) {
        if (layer_enc_col.constructor.name === "String") { // given as a field name
          encoding.column.field = layer_enc_col;
          encoding.column.type = datatype[layer_enc_col]?.type;
        } else if (layer_enc_col !== undefined) { // given as an object
          if (layer_enc_col.field !== undefined && encoding.column.field != layer_enc_col.field) {
            encoding.column.field = layer_enc_col.field;
            encoding.column.type = (layer_enc_col?.type !== undefined ? layer_enc_col.type : datatype[layer_enc_col.field]?.type);
          }
          if (layer_enc_col.sort == null) {
            encoding.column.sort = null;
          } else if (layer_enc_col.sort !== undefined) { // sorting
            if (layer_enc_col.sort.constructor.name === "String") { //by a field
              encoding.column.sort = layer_enc_col.sort;
            } else if (layer_enc_col.sort.constructor.name === "Object") { // more details
              if (layer_enc_col.sort.field !== undefined && layer_enc_col.sort.order?.constructor.name === "String") { // by a field
                encoding.column.sort = { "descending": "-", "ascending": "" }[layer_enc_col.sort.order] + "datum." + layer_enc_col.sort.field;
              } else if (layer_enc_col.sort.field !== undefined && layer_enc_col.sort.order === undefined) { // no particular order is provided
                encoding.column.sort = "datum." + layer_enc_col.sort.field;
              } else if (layer_enc_col.sort.order?.constructor.name === "Array") { // particular order is provided
                encoding.column.sort = layer_enc_col.sort.order;
              }
            }
            if (layer_enc_col.sort.reverse !== undefined && layer_enc_col.sort.reverse == true) { // if reversed
              if (encoding.column.sort?.constructor.name === "Array") encoding.column.sort = encoding.column.sort.reverse();
            }
            if (layer_enc_col.sort.order) { // register the sort order in the datatype object
              datatype[encoding.column.field].order = (encoding.column.sort.reverse == true ? encoding.column.sort.order.reverse() : encoding.column.sort.order);
              if (layer_enc_col.sort.field !== undefined && layer_enc_col.sort.order === "ascending") {
                datatype[encoding.column.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[layer_enc_col.sort.field] - b[layer_enc_col.sort.field]; }).map(d => d[encoding.column.field]);
              } else if (layer_enc_col.sort.field !== undefined && layer_enc_col.sort.order === "descending") {
                datatype[encoding.column.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[layer_enc_col.sort.field] - a[layer_enc_col.sort.field]; }).map(d => d[encoding.column.field]);
              }
            }
          }
        }
        if (layer_enc_col.aggregate) {
          encoding.column.aggregate = layer_enc_col.aggregate;
        }
        if (layer_enc_col.bin) {
          encoding.column.bin = layer_enc_col.bin;
        }
      }
      if (encoding.col && !encoding.col?.type) delete encoding.col.type;
    }
    if (enc_row) {
      // define row encoding
      encoding.row = {};
      if (enc_row.constructor.name === "String") {
        encoding.row.field = enc_row;
        encoding.row.type = datatype[enc_row]?.type;
      } else {
        encoding.row.field = enc_row.field;
        encoding.row.type = (enc_row?.type !== undefined ? enc_row.type : datatype[enc_row.field]?.type);
        if (enc_row.sort == null) {
          encoding.row.sort = null;
        } else if (enc_row.sort !== undefined) {
          if (enc_row.sort.constructor.name === "String") {
            encoding.row.sort = enc_row.sort;
          } else if (enc_row.sort.constructor.name === "Object") {
            if (enc_row.sort.field !== undefined && enc_row.sort.order?.constructor.name === "String") {
              encoding.row.sort = { "descending": "-", "ascending": "" }[enc_row.sort.order] + "datum." + enc_row.sort.field;
            } else if (enc_row.sort.field !== undefined && enc_row.sort.order === undefined) {
              encoding.row.sort = "datum." + enc_row.sort.field;
            } else if (enc_row.sort.order?.constructor.name === "Array") {
              encoding.row.sort = enc_row.sort.order;
            }
          }
          if (enc_row.sort.reverse !== undefined && enc_row.sort.reverse == true) {
            if (encoding.row.sort?.constructor.name === "Array") encoding.row.sort = encoding.row.sort.reverse();
          }
          if (enc_row.sort.order) {
            datatype[enc_row.field].order = (enc_row.sort.reverse == true ? enc_row.sort.order.reverse() : enc_row.sort.order);
            if (enc_row.sort.field !== undefined && enc_row.sort.order === "ascending") {
              datatype[enc_row.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[enc_row.sort.field] - b[enc_row.sort.field]; }).map(d => d[enc_row.field]);
            } else if (enc_row.sort.field !== undefined && enc_row.sort.order === "descending") {
              datatype[enc_row.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[enc_row.sort.field] - a[enc_row.sort.field]; }).map(d => d[enc_row.field]);
            }
          }
        }
      }
      if (enc_row.aggregate) {
        encoding.row.aggregate = enc_row.aggregate;
      }
      if (enc_row.bin) {
        encoding.row.bin = enc_row.bin;
      }
      // axis design --> only text (when dealing text)
      encoding.row.header = {};
      encoding.row.header.title = null;
      encoding.row.header.labels = false;
      encoding.row.header.labelAngle = 0;

      // layer specific
      if (layer_enc_row !== undefined) {
        if (layer_enc_row.constructor.name === "String") { // given as a field name
          encoding.row.field = layer_enc_row;
          encoding.row.type = datatype[layer_enc_row]?.type;
        } else if (layer_enc_row !== undefined) { // given as an object
          if (layer_enc_row.field !== undefined && encoding.row.field != layer_enc_row.field) {
            encoding.row.field = layer_enc_row.field;
            encoding.row.type = (layer_enc_row?.type !== undefined ? layer_enc_row.type : datatype[layer_enc_row.field]?.type);
          }
          if (layer_enc_row.sort == null) {
            encoding.row.sort = null;
          } else if (layer_enc_row.sort !== undefined) { // sorting
            if (layer_enc_row.sort.constructor.name === "String") { //by a field
              encoding.row.sort = layer_enc_row.sort;
            } else if (layer_enc_row.sort.constructor.name === "Object") { // more details
              if (layer_enc_row.sort.field !== undefined && layer_enc_row.sort.order?.constructor.name === "String") { // by a field
                encoding.row.sort = { "descending": "-", "ascending": "" }[layer_enc_row.sort.order] + "datum." + layer_enc_row.sort.field;
              } else if (layer_enc_row.sort.field !== undefined && layer_enc_row.sort.order === undefined) { // no particular order is provided
                encoding.row.sort = "datum." + layer_enc_row.sort.field;
              } else if (layer_enc_row.sort.order?.constructor.name === "Array") { // particular order is provided
                encoding.row.sort = layer_enc_row.sort.order;
              }
            }
            if (layer_enc_row.sort.reverse !== undefined && layer_enc_row.sort.reverse == true) { // if reversed
              if (encoding.row.sort?.constructor.name === "Array") encoding.row.sort = encoding.row.sort.reverse();
            }
            if (layer_enc_row.sort.order) { // register the sort order in the datatype object
              datatype[encoding.row.field].order = (encoding.row.sort.reverse == true ? encoding.row.sort.order.reverse() : encoding.row.sort.order);
              if (layer_enc_row.sort.field !== undefined && layer_enc_row.sort.order === "ascending") {
                datatype[encoding.row.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[layer_enc_row.sort.field] - b[layer_enc_row.sort.field]; }).map(d => d[encoding.row.field]);
              } else if (layer_enc_row.sort.field !== undefined && layer_enc_row.sort.order === "descending") {
                datatype[encoding.row.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[layer_enc_row.sort.field] - a[layer_enc_row.sort.field]; }).map(d => d[encoding.row.field]);
              }
            }
          }
        }
        if (layer_enc_row.aggregate) {
          encoding.row.aggregate = layer_enc_row.aggregate;
        }
        if (layer_enc_row.bin) {
          encoding.row.bin = layer_enc_row.bin;
        }
      }
      if (encoding.row && !encoding.row?.type) delete encoding.row.type;
    }
    // x 
    if (enc_x) {
      // define column encoding
      encoding.x = {};

      if (enc_x.constructor.name === "String") {
        encoding.x.field = enc_x;
        encoding.x.type = datatype[enc_x]?.type;
      } else if (enc_x.constructor.name === "Array") {
        let enc_x1 = enc_x[0], enc_x2 = enc_x[1];
        encoding.x.field = (enc_x1.constructor.name === "String" ? enc_x1 : enc_x1.field);
        encoding.x.type = (enc_x.type !== undefined ? enc_x.type : datatype[enc_x1]?.type);
        if (enc_x1.aggregate) {
          encoding.x.aggregate = enc_x1.aggregate;
        } else if (enc_x.aggregate) {
          encoding.x.aggregate = enc_x.aggregate;
        }
        if (enc_x1.bin) {
          encoding.x.bin = enc_x1.bin;
        } else if (enc_x.bin) {
          encoding.x.bin = enc_x.bin;
        }
        if (enc_x2) {
          encoding.x2 = {};
          encoding.x2.field = (enc_x2.constructor.name === "String" ? enc_x2 : enc_x2.field);
          encoding.x2.type = datatype[enc_x2]?.type;
          if (encoding.x && !encoding.x?.type) delete encoding.x.type;
          if (enc_x2.aggregate) {
            encoding.x2.aggregate = enc_x2.aggregate;
          } else if (enc_x.aggregate) {
            encoding.x2.aggregate = enc_x.aggregate;
          }
          if (enc_x2.bin) {
            encoding.x2.bin = enc_x2.bin;
          } else if (enc_x.bin) {
            encoding.x2.bin = enc_x.bin;
          }
        }
      } else {
        if (enc_x.field?.constructor.name === "Array") {
          let enc_x1 = enc_x.field[0], enc_x2 = enc_x.field[1];
          encoding.x.field = enc_x1;
          encoding.x.type = (enc_x.type !== undefined ? enc_x.type : datatype[enc_x1]?.type);
          if (enc_x.aggregate) {
            encoding.x.aggregate = enc_x.aggregate;
          }
          if (enc_x.bin) {
            encoding.x.bin = enc_x.bin;
          }
          if (enc_x2) { // when second field is provided; (eventually return in the length encoding)
            encoding.x2 = {};
            encoding.x2.field = enc_x2;
            encoding.x2.type = datatype[enc_x2]?.type;
            if (enc_x.aggregate) {
              encoding.x2.aggregate = enc_x.aggregate;
            }
            if (enc_x.bin) {
              encoding.x2.bin = enc_x.bin;
            }
          }
        } else {
          if (enc_x.field) {
            encoding.x.field = enc_x.field;
            encoding.x.type = (enc_x.type !== undefined ? enc_x.type : datatype[enc_x.field].type);
          }
          if (enc_x.aggregate) {
            encoding.x.aggregate = enc_x.aggregate;
          }
          if (enc_x.bin) {
            encoding.x.bin = enc_x.bin;
          }
        }
        if (enc_x.sort == null) {
          encoding.x.sort = null;
        } else if (enc_x.sort !== undefined) {
          if (enc_x.sort.constructor.name === "String") {
            encoding.x.sort = enc_x.sort;
          } else if (enc_x.sort.constructor.name === "Object") {
            if (enc_x.sort.field !== undefined && enc_x.sort.order?.constructor.name === "String") {
              encoding.x.sort = { "descending": "-", "ascending": "" }[enc_x.sort.order] + "datum." + enc_x.sort.field;
            } else if (enc_x.sort.field !== undefined && enc_x.sort.order === undefined) {
              encoding.x.sort = "datum." + enc_x.sort.field;
            } else if (enc_x.sort.order?.constructor.name === "Array") {
              encoding.x.sort = enc_x.sort.order;
            }
          }
          if (enc_x.sort.reverse !== undefined && enc_x.sort.reverse == true) {
            if (encoding.x.sort?.constructor.name === "Array") encoding.x.sort = encoding.x.sort.reverse();
            else if (encoding.x.sort?.constructor.name === "String") encoding.x.sort = { "ascending": "descending", "descending": "ascending" }[encoding.x.sort]
            else encoding.x.sort = "descending";
          }
          if (enc_x.sort.order) {
            datatype[enc_x.field].order = (enc_x.sort.reverse == true ? enc_x.sort.order.reverse() : enc_x.sort.order);
            if (enc_x.sort.field !== undefined && enc_x.sort.order === "ascending") {
              datatype[enc_x.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[enc_x.sort.field] - b[enc_x.sort.field]; }).map(d => d[enc_x.field]);
            } else if (enc_x.sort.field !== undefined && enc_x.sort.order === "descending") {
              datatype[enc_x.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[enc_x.sort.field] - a[enc_x.sort.field]; }).map(d => d[enc_x.field]);
            }
          }
        }
        if (encoding.x && !encoding.x?.type) delete encoding.x.type;
      }
      // stack
      if (enc_x.stack !== undefined) {
        encoding.x.stack = enc_x.stack;
      }
      // scale configure
      encoding.x.scale = {};
      encoding.x.axis = {};
      // axis design 
      if (spec.layout?.haxis) {
        let haxis = spec.layout.haxis;
        encoding.x.scale.zero = (haxis.zero === true ? true : false); // scaling to zero
        encoding.x.scale.type = (haxis.type ? haxis.type : "linear"); // scale type
        if (haxis.offset) encoding.x.axis.offset = haxis.offset; // offset from the view
        encoding.x.axis.domain = (haxis.domain === true ? true : false); // domain line!
        if (haxis.domainColor && haxis.domain === true) encoding.x.axis.domainColor = haxis.domainColor;
        if (haxis.domainOpacity && haxis.domain === true) encoding.x.axis.domainOpacity = haxis.domainOpacity;
        if (haxis.domainWidth && haxis.domain === true) encoding.x.axis.domainWidth = haxis.domainWidth;
        if (haxis.domainDash && haxis.domain === true) encoding.x.axis.domainDash = haxis.domainDash;
        encoding.x.axis.grid = (haxis.grid === true ? true : false); // grid line!
        if (haxis.gridColor && haxis.grid === true) encoding.x.axis.gridColor = haxis.gridColor;
        if (haxis.gridDash && haxis.grid === true) encoding.x.axis.gridDash = haxis.gridDash;
        if (haxis.gridOpacity && haxis.grid === true) encoding.x.axis.gridOpacity = haxis.gridOpacity;
        if (haxis.gridWidth && haxis.grid === true) encoding.x.axis.gridWidth = haxis.gridWidth;
        if (haxis.orient !== undefined) encoding.x.axis.orient = haxis.orient;
      } else { // default when haxis is not defined
        encoding.x.axis.domain = false;
        encoding.x.axis.grid = false;
      }
      encoding.x.axis.labels = false;
      encoding.x.axis.title = null;
      encoding.x.axis.ticks = false;
      if (enc_x.scale !== undefined) {
        Object.keys(enc_x.scale).forEach(key => {
          encoding.x.scale[key] = enc_x.scale[key];
        })
      }
      // layer-specific adjustment
      if (layer_enc_x !== undefined) {
        if (layer_enc_x.constructor.name === "String") {
          encoding.x.field = layer_enc_x;
          encoding.x.type = datatype[layer_enc_x].type;
        } else if (layer_enc_x.constructor.name === "Array") {
          let layer_enc_x1 = layer_enc_x[0], layer_enc_x2 = layer_enc_x[1];
          encoding.x.field = (layer_enc_x1.constructor.name === "String" ? layer_enc_x1 : layer_enc_x1.field);
          encoding.x.type = (layer_enc_x1.type !== undefined ? layer_enc_x1.type : datatype[encoding.x.field].type);
          if (layer_enc_x1.aggregate) {
            encoding.x.aggregate = layer_enc_x1.aggregate;
          } else if (layer_enc_x.aggregate) {
            encoding.x.aggregate = layer_enc_x.aggregate;
          }
          if (layer_enc_x1.bin) {
            encoding.x.bin = layer_enc_x1.bin;
          } else if (layer_enc_x.bin) {
            encoding.x.bin = layer_enc_x.bin;
          }
          if (layer_enc_x2) {
            encoding.x2 = {};
            encoding.x2.field = (layer_enc_x2.constructor.name === "String" ? layer_enc_x2 : layer_enc_x2.field);
            encoding.x2.type = datatype[encoding.x2.field].type;
            if (layer_enc_x2.aggregate) {
              encoding.x2.aggregate = layer_enc_x2.aggregate;
            } else if (layer_enc_x.aggregate) {
              encoding.x2.aggregate = layer_enc_x.aggregate;
            }
            if (layer_enc_x2.bin) {
              encoding.x2.bin = layer_enc_x2.bin;
            } else if (layer_enc_x.bin) {
              encoding.x2.bin = layer_enc_x.bin;
            }
          }
        } else {
          if (layer_enc_x.field !== undefined && encoding.x.field !== layer_enc_x.field) {
            encoding.x.field = layer_enc_x.field;
          }
          encoding.x.type = (layer_enc_x?.type !== undefined ? layer_enc_x.type : datatype[encoding.x.field].type);
          if (layer_enc_x.aggregate) {
            encoding.x.aggregate = layer_enc_x.aggregate;
          }
          if (layer_enc_x.bin) {
            encoding.x.bin = layer_enc_x.bin;
          }

          if (layer_enc_x.sort == null) {
            encoding.x.sort = null;
          } else if (layer_enc_x.sort !== undefined) {
            if (layer_enc_x.sort.constructor.name === "String") {
              encoding.x.sort = layer_enc_x.sort;
            } else if (layer_enc_x.sort.constructor.name === "Object") {
              if (layer_enc_x.sort.field !== undefined && layer_enc_x.sort.order?.constructor.name === "String") {
                encoding.x.sort = { "descending": "-", "ascending": "" }[layer_enc_x.sort.order] + "datum." + layer_enc_x.sort.field;
              } else if (layer_enc_x.sort.field !== undefined && layer_enc_x.sort.order === undefined) {
                encoding.x.sort = "datum." + layer_enc_x.sort.field;
              } else if (layer_enc_x.sort.order?.constructor.name === "Array") {
                encoding.x.sort = layer_enc_x.sort.order;
              }
            }
            if (layer_enc_x.sort.reverse !== undefined && layer_enc_x.sort.reverse == true) {
              if (encoding.x.sort?.constructor.name === "Array") encoding.x.sort = encoding.x.sort.reverse();
              else if (encoding.x.sort?.constructor.name === "String") encoding.x.sort = { "ascending": "descending", "descending": "ascending" }[encoding.x.sort]
              else encoding.x.sort = "descending";
            }
            if (layer_enc_x.sort.order) {
              datatype[encoding.x.field].order = (layer_enc_x.sort.reverse == true ? encoding.x.sort.order.reverse() : encoding.x.sort.order);
              if (layer_enc_x.sort.field !== undefined && layer_enc_x.sort.order === "ascending") {
                datatype[encoding.x.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[layer_enc_x.sort.field] - b[layer_enc_x.sort.field]; }).map(d => d[encoding.x.field]);
              } else if (layer_enc_x.sort.field !== undefined && layer_enc_x.sort.order === "descending") {
                datatype[encoding.x.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[layer_enc_x.sort.field] - a[layer_enc_x.sort.field]; }).map(d => d[encoding.x.field]);
              }
            }
          }
        }

        if (layer_enc_x.scale !== undefined) {
          Object.keys(layer_enc_x.scale).forEach(key => {
            encoding.x.scale[key] = layer_enc_x.scale[key];
          })
        }
      }
      // label design --> only text (when dealing text)
    }
    // y
    if (enc_y) {
      // define column encoding
      encoding.y = {};
      if (enc_y.constructor.name === "String") {
        encoding.y.field = enc_y;
        encoding.y.type = datatype[enc_y]?.type;
      } else if (enc_y.constructor.name === "Array") {
        let enc_y1 = enc_y[0], enc_y2 = enc_y[1];
        encoding.y.field = enc_y1;
        encoding.y.type = (enc_y.type !== undefined ? enc_y.type : datatype[enc_y1]?.type);
        if (enc_y1.aggregate) {
          encoding.y.aggregate = enc_y1.aggregate;
        } else if (enc_y.aggregate) {
          encoding.y.aggregate = enc_y.aggregate;
        }
        if (enc_y1.bin) {
          encoding.y.bin = enc_y1.bin;
        } else if (enc_y.bin) {
          encoding.y.bin = enc_y.bin;
        }
        if (enc_y2) {
          encoding.y2 = {};
          encoding.y2.field = enc_y2;
          encoding.y2.type = datatype[enc_y2]?.type;
          if (enc_y2.aggregate) {
            encoding.y2.aggregate = enc_y2.aggregate;
          } else if (enc_y.aggregate) {
            encoding.y2.aggregate = enc_y.aggregate;
          }
          if (enc_y2.bin) {
            encoding.y2.bin = enc_y2.bin;
          } else if (enc_y.bin) {
            encoding.y2.bin = enc_y.bin;
          }
        }
      } else {
        if (enc_y.field?.constructor.name === "Array") {
          let enc_y1 = enc_y.field[0], enc_y2 = enc_y.field[1];
          encoding.y.field = enc_y1;
          encoding.y.type = (enc_y.type !== undefined ? enc_y.type : datatype[enc_y1]?.type);
          if (enc_y.aggregate) {
            encoding.y.aggregate = enc_y.aggregate;
          }
          if (enc_y.bin) {
            encoding.y.bin = enc_y.bin;
          }
          if (enc_y2) {
            encoding.y2 = {};
            encoding.y2.field = enc_y2;
            encoding.y2.type = datatype[enc_y2].type;
            if (enc_y.aggregate) {
              encoding.y2.aggregate = enc_y.aggregate;
            }
            if (enc_y.bin) {
              encoding.y2.bin = enc_y.bin;
            }
          }
        } else {
          if (enc_y.field) {
            encoding.y.field = enc_y.field;
            encoding.y.type = (enc_y.type !== undefined ? enc_y.type : datatype[enc_y.field]?.type);
          }
          if (enc_y.aggregate) {
            encoding.y.aggregate = enc_y.aggregate;
          }
          if (enc_y.bin) {
            encoding.y.bin = enc_y.bin;
          }
        }
        if (enc_y.sort == null) {
          encoding.y.sort = null;
        } else if (enc_y.sort !== undefined) {
          if (enc_y.sort.constructor.name === "String") {
            encoding.y.sort = enc_y.sort;
          } else if (enc_y.sort.constructor.name === "Object") {
            if (enc_y.sort.field !== undefined && enc_y.sort.order?.constructor.name === "String") {
              encoding.y.sort = { "descending": "-", "ascending": "" }[enc_y.sort.order] + "datum." + enc_y.sort.field;
            } else if (enc_y.sort.field !== undefined && enc_y.sort.order === undefined) {
              encoding.y.sort = "datum." + enc_y.sort.field;
            } else if (enc_y.sort.order?.constructor.name === "Array") {
              encoding.y.sort = enc_y.sort.order;
            }
          }
          if (enc_y.sort.reverse !== undefined && enc_y.sort.reverse == true) {
            if (encoding.y.sort?.constructor.name === "Array") encoding.y.sort = encoding.y.sort.reverse();
            else if (encoding.y.sort?.constructor.name === "String") encoding.y.sort = { "ascending": "descending", "descending": "ascending" }[encoding.y.sort]
            else encoding.y.sort = "descending";
          }
          if (enc_y.sort.order) {
            datatype[enc_y.field].order = (enc_y.sort.reverse == true ? enc_y.sort.order.reverse() : enc_y.sort.order);
            if (enc_y.sort.field !== undefined && enc_y.sort.order === "ascending") {
              datatype[enc_y.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[enc_y.sort.field] - b[enc_y.sort.field]; }).map(d => d[enc_y.field]);
            } else if (enc_y.sort.field !== undefined && enc_y.sort.order === "descending") {
              datatype[enc_y.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[enc_y.sort.field] - a[enc_y.sort.field]; }).map(d => d[enc_y.field]);
            }
          }
        }
        if (encoding.y && !encoding.y?.type) delete encoding.y.type;
      }
      // stack
      if (enc_y.stack !== undefined) {
        encoding.y.stack = enc_y.stack;
      }
      // scale configure
      encoding.y.scale = {};
      encoding.y.axis = {};
      // axis design
      if (spec.layout?.vaxis) {
        let vaxis = spec.layout.vaxis;
        encoding.y.scale.zero = (vaxis.zero === true ? true : false); // scaling to zero
        encoding.y.scale.type = (vaxis.type ? vaxis.type : "linear"); // scale type
        if (vaxis.offset) encoding.y.axis.offset = vaxis.offset; // offset from the view
        encoding.y.axis.domain = (vaxis.domain === true ? true : false); // domain line!
        if (vaxis.domainColor && vaxis.domain === true) encoding.y.axis.domainColor = vaxis.domainColor;
        if (vaxis.domainOpacity && vaxis.domain === true) encoding.y.axis.domainOpacity = vaxis.domainOpacity;
        if (vaxis.domainWidth && vaxis.domain === true) encoding.y.axis.domainWidth = vaxis.domainWidth;
        if (vaxis.domainDash && vaxis.domain === true) encoding.y.axis.domainDash = vaxis.domainDash;
        encoding.y.axis.grid = (vaxis.grid === true ? true : false); // grid line!
        if (vaxis.gridColor && vaxis.grid === true) encoding.y.axis.gridColor = vaxis.gridColor;
        if (vaxis.gridDash && vaxis.grid === true) encoding.y.axis.gridDash = vaxis.gridDash;
        if (vaxis.gridOpacity && vaxis.grid === true) encoding.y.axis.gridOpacity = vaxis.gridOpacity;
        if (vaxis.gridWidth && vaxis.grid === true) encoding.y.axis.gridWidth = vaxis.gridWidth;
      } else { // default when vaxis is not defined
        encoding.y.axis.domain = false;
        encoding.y.axis.grid = false;
      }
      encoding.y.axis.labels = false;
      encoding.y.axis.title = null;
      encoding.y.axis.ticks = false;
      // label design --> only text (when dealing text)
      if (enc_y.scale !== undefined) {
        Object.keys(enc_y.scale).forEach(key => {
          encoding.y.scale[key] = enc_y.scale[key];
        })
      }
      // label specific adjustment
      if (layer_enc_y !== undefined) {
        if (layer_enc_y.constructor.name === "String") {
          encoding.y.field = layer_enc_y;
          encoding.y.type = datatype[layer_enc_y].type;
        } else if (layer_enc_y.constructor.name === "Array") {
          let layer_enc_y1 = layer_enc_y[0], layer_enc_y2 = layer_enc_y[1];
          encoding.y.field = (layer_enc_y1.constructor.name === "String" ? layer_enc_y1 : layer_enc_y1.field);
          encoding.y.type = (layer_enc_y1.type !== undefined ? layer_enc_y1.type : datatype[encoding.y.field].type);
          if (layer_enc_y1.aggregate) {
            encoding.y.aggregate = layer_enc_y1.aggregate;
          } else if (layer_enc_y.aggregate) {
            encoding.y.aggregate = layer_enc_y.aggregate;
          }
          if (layer_enc_y1.bin) {
            encoding.y.bin = layer_enc_y1.bin;
          } else if (layer_enc_y.bin) {
            encoding.y.bin = layer_enc_y.bin;
          }
          if (layer_enc_y2) {
            encoding.y2 = {};
            encoding.y2.field = (layer_enc_y2.constructor.name === "String" ? layer_enc_y2 : layer_enc_y2.field);
            encoding.y2.type = datatype[encoding.y2.field].type;
            if (layer_enc_y2.aggregate) {
              encoding.y2.aggregate = layer_enc_y2.aggregate;
            } else if (layer_enc_y.aggregate) {
              encoding.y2.aggregate = layer_enc_y.aggregate;
            }
            if (layer_enc_y2.bin) {
              encoding.y2.bin = layer_enc_y2.bin;
            } else if (layer_enc_y.bin) {
              encoding.y2.bin = layer_enc_y.bin;
            }
          }
        } else {
          if (layer_enc_y.field !== undefined && encoding.y.field !== layer_enc_y.field) {
            encoding.y.field = layer_enc_y.field;
          }
          encoding.y.type = (layer_enc_y?.type !== undefined ? layer_enc_y.type : datatype[encoding.y.field].type);
          if (layer_enc_y.aggregate) {
            encoding.y.aggregate = layer_enc_y.aggregate;
          }
          if (layer_enc_y.bin) {
            encoding.y.bin = layer_enc_y.bin;
          }

          if (layer_enc_y.sort == null) {
            encoding.y.sort = null;
          } else if (layer_enc_y.sort !== undefined) {
            if (layer_enc_y.sort.constructor.name === "String") {
              encoding.y.sort = layer_enc_y.sort;
            } else if (layer_enc_y.sort.constructor.name === "Object") {
              if (layer_enc_y.sort.field !== undefined && layer_enc_y.sort.order?.constructor.name === "String") {
                encoding.y.sort = { "descending": "-", "ascending": "" }[layer_enc_y.sort.order] + "datum." + layer_enc_y.sort.field;
              } else if (layer_enc_y.sort.field !== undefined && layer_enc_y.sort.order === undefined) {
                encoding.y.sort = "datum." + layer_enc_y.sort.field;
              } else if (layer_enc_y.sort.order?.constructor.name === "Array") {
                encoding.y.sort = layer_enc_y.sort.order;
              }
            }
            if (layer_enc_y.sort.reverse !== undefined && layer_enc_y.sort.reverse == true) {
              if (encoding.y.sort?.constructor.name === "Array") encoding.y.sort = encoding.y.sort.reverse();
              else if (encoding.y.sort?.constructor.name === "String") encoding.y.sort = { "ascending": "descending", "descending": "ascending" }[encoding.y.sort]
              else encoding.y.sort = "descending";
            }
            if (layer_enc_y.sort.order) {
              datatype[encoding.y.field].order = (layer_enc_y.sort.reverse == true ? encoding.y.sort.order.reverse() : encoding.y.sort.order);
              if (layer_enc_y.sort.field !== undefined && layer_enc_y.sort.order === "ascending") {
                datatype[encoding.y.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return a[layer_enc_y.sort.field] - b[layer_enc_y.sort.field]; }).map(d => d[encoding.y.field]);
              } else if (layer_enc_y.sort.field !== undefined && layer_enc_y.sort.order === "descending") {
                datatype[encoding.y.field].order = Fronto.deepcopy(spec.data).sort((a, b) => { return b[layer_enc_y.sort.field] - a[layer_enc_y.sort.field]; }).map(d => d[encoding.y.field]);
              }
            }
          }
        }

        if (layer_enc_y.scale !== undefined) {
          Object.keys(layer_enc_y.scale).forEach(key => {
            encoding.y.scale[key] = layer_enc_y.scale[key];
          })
        }
      }
    }
    // encodings or values
    let property_channels = [
      "color", "stroke", "fillOpacity", "strokeOpacity", "opacity",
      "shape", "strokeDash", "strokeWidth", "size", "url", "fill",
      "angle", "theta", "radius", "radius2", "detail", "order"]
    property_channels.forEach(channel => {
      if (layer_info.mark?.[channel]) {
        let prop_info = layer_info.mark[channel];
        if (prop_info.field !== undefined) { // encoding setting
          encoding[channel] = {};
          // in case it has interactive filter
          let has_interactive_filter = spec.interaction?.filter?.includes(prop_info.field);
          if (has_interactive_filter) {
            encoding.condition = {
              "name": "filter_" + prop_info.field,
              "field": prop_info.field,
              "type": (prop_info.type ? prop_info.type : datatype[prop_info.field].type)
            }
            let paramObj = {
              "name": "filter_" + prop_info.field,
              "select": {
                "type": "point",
                "fields": [prop_info.field]
              }
            };
            if (datatype[prop_info.field].type === "nominal") {
              paramObj.bind = { "input": "range", "min": datatype[prop_info.field].min, "max": datatype[prop_info.field].max };
            } else {
              paramObj.bind = { "input": "select", "options": datatype[prop_info.field].values };
            }
            params.push(paramObj)
            if (prop_info.timeUnit) encoding[channel].condition.timeUnit = prop_info.timeUnit;
            if (prop_info.scale) {
              let scale = Fronto.deepcopy(prop_info.scale);
              if (scale.reverse == true) {
                scale.sort = "descending";
                delete scale.reverse;
              }
              encoding[channel].condition.scale = scale;
            }
            if (prop_info.value !== undefined) { // direct value setting
              encoding[channel].value = prop_info.value;
            }
          } else {
            encoding[channel].field = prop_info.field;
            encoding[channel].type = (prop_info.type ? prop_info.type : datatype[prop_info.field].type);
            if (prop_info.timeUnit) encoding[channel].timeUnit = prop_info.timeUnit;
            if (prop_info.scale) {
              let scale = Fronto.deepcopy(prop_info.scale);
              if (scale.reverse == true) {
                scale.sort = "descending";
                delete scale.reverse;
              }
              encoding[channel].scale = scale;
              if (encoding[channel].scale.domain && !datatype[prop_info.field].order) datatype[prop_info.field].order = encoding[channel].scale.domain;
            }
            if (prop_info.sort !== undefined) {
              if (prop_info.sort.constructor.name === "String") {
                encoding[channel].sort = prop_info.sort;
              } else if (prop_info.sort.order?.constructor.nam === "Array") {
                encoding[channel].sort = prop_info.sort.order;
              }
            }
          }
          if (prop_info.aggregate) encoding[channel].aggregate = prop_info.aggregate;
          if (prop_info.bin) encoding[channel].bin = prop_info.bin;
          encoding[channel].legend = null;
        } else {
          if (prop_info.value !== undefined) { // direct value setting
            if (layer_info.mark.type === "area" && channel == "strokeWidth") {
              mark.line = true;
            } else {
              // mark[channel] = {};
              mark[channel] = prop_info.value;
            }
          }
        }
        if (prop_info.conditions !== undefined) {
          if (encoding[channel] === undefined) encoding[channel] = {};
          if (encoding[channel]?.condition === undefined) encoding[channel].condition = [];
          prop_info.conditions.forEach(cond => {
            if (cond.condition && cond.value)
              encoding[channel].condition.push({ test: cond.condition, value: cond.value });
          })
          // not to impact legend
          if (prop_info.value !== undefined) {
            encoding[channel].value = prop_info.value;
          } else {
            encoding[channel].value = null;
          }
        }
      }
    })
    if (["area", "line"].includes(layer_info.mark?.type) && layer_info.mark?.point !== undefined) {
      mark.point = layer_info.mark.point
    }
    if (layer_info.mark?.type === "line" && encoding.stroke !== undefined && encoding.color === undefined) {
      encoding.color = Fronto.deepcopy(encoding.stroke);
    }
    if (layer_info.mark?.type === "bar" && layer_info.mark.cornerRadius) {
      mark.cornerRadius = layer_info.mark.cornerRadius;
    }

    if (layer_info.mark?.type === "bar" && layer_info.mark.cornerRadiusEnd) {
      mark.cornerRadiusEnd = layer_info.mark.cornerRadiusEnd;
    }
    // width height
    if (layer_info.mark?.width !== undefined) mark.width = layer_info.mark.width;
    if (layer_info.mark?.height !== undefined) mark.height = layer_info.mark.height;


    // check binning
    let x_bin = Fronto.get_bin_steps("x", encoding, datatype);

    // line mark direction
    let line_direction = layer_info.mark?.type === "line" ? Fronto.getLineDirection(encoding.x.field, encoding.y.field, dataset, datatype) : null;

    let sublayer_linemark_text = [];

    // line end mark
    let lineEndMark_adjust = [];
    if (layout_type === "layered" && layer_info.mark?.type === "line" && layer_info.mark?.endMark !== undefined) {
      // internal
      let $sublayer_linemark_end_mark = {
        "name": "F_layer_" + i + "__endMark",
        "mark": { "type": "circle" },
        "encoding": {}
      };

      if (line_direction === "horizontal") {
        $sublayer_linemark_end_mark.encoding = {
          "size": { "value": 45 },
          "x": { "aggregate": "max", "field": encoding.x.field, "type": encoding.x.type },
          "y": { "aggregate": { "argmax": encoding.x.field }, "field": encoding.y.field, "type": encoding.y.type }
        };
      } else {
        $sublayer_linemark_end_mark.encoding = {
          "size": { "value": 45 },
          "x": { "aggregate": { "argmax": encoding.y.field }, "field": encoding.x.field, "type": encoding.x.type },
          "y": { "aggregate": "max", "field": encoding.y.field, "type": encoding.y.type }
        };
      }
      if (layer_info.mark.endMark.type !== undefined) {
        $sublayer_linemark_end_mark.mark.type = layer_info.mark.endMark.type;
      }
      if (layer_info.mark.endMark.size !== undefined) {
        $sublayer_linemark_end_mark.encoding.size = layer_info.mark.endMark.size;
      }
      if (x_bin !== null) {
        if ($sublayer_linemark_end_mark.transform === undefined) $sublayer_linemark_end_mark.transform = [];
        $sublayer_linemark_end_mark.transform.push({
          "filter": "datum." + x_bin.field + " >= " + (x_bin.stop - x_bin.step)
        });
        delete $sublayer_linemark_end_mark.encoding.x;
        delete $sublayer_linemark_end_mark.encoding.y;
      }
      sublayer_linemark_text.push($sublayer_linemark_end_mark);
      lineEndMark_adjust = ["color", "detail"];
    }

    // label
    let label_axis = layer_info.text?.filter(d => d.type === "on-axis");
    let label_legend = layer_info.text?.filter(d => d.type === "legend");
    let label_mark = layer_info.text?.filter(d => d.type === "on-mark"); // nondata

    // axis labels
    let horizontal_space = 0, column_label_adjust_for_serialization, row_label_adjust_for_serialization;
    label_axis?.forEach((labelItem, lai) => {
      let targetAxis = Object.keys(encoding).filter(channel => {
        return ["x", "y", "x2", "y2", "row", "column"].includes(channel) && (Fronto.simpleNotUndefinedEq(encoding[channel].field, labelItem.field) || Fronto.simpleNotUndefinedEq(encoding[channel].aggregate === labelItem.aggregate));
      })
      let tickValues = labelItem.tick?.tickValues;
      targetAxis.forEach(axis => { // axis labels and ticks
        let axisDesign = (axis === "row" || axis === "column" ? encoding[axis].header : encoding[axis].axis);
        axisDesign.labels = true;
        let isSerial = false;
        if (labelItem.serial === true && ["bar", "rect", "image", "square"].includes(layer_info.mark?.type)) {
          // bar-like marks for serialization
          if (encoding.x.type === encoding.y.type && encoding.y.type !== undefined) isSerial = "vertical";
          else {
            if (encoding.y.type === "quantitative") isSerial = "vertical";
            else if (encoding.y.type === "nominal") isSerial = "horizontal";
            else if (encoding.x.type === "quantitative") isSerial = "horizontal";
            else if (encoding.x.type === "nominal") isSerial = "vertical";
            else if (encoding.x.type === "ordinal" && encoding.y.type === "temporal") isSerial = "horizontal";
            else if (encoding.x.type === "temporal" && encoding.y.type === "ordinal") isSerial = "vertical";
          }
        } else if (labelItem.orient !== undefined && axisDesign.orient === undefined) axisDesign.orient = labelItem.orient;

        let fixedValue = labelItem.values !== undefined;
        if (fixedValue) axisDesign.values = labelItem.values;
        let differentLabelTickValues = fixedValue && tickValues && !Fronto.arrayIdentity(tickValues, axisDesign.values);
        if (differentLabelTickValues && ["quantitative", "temporal"].includes(encoding[axis].type) && ["x", "y"].includes(axis)) {
          let newValues = [];
          newValues.push(...tickValues);
          newValues.push(...axisDesign.values);
          newValues = Fronto.unique(newValues).sort();
          axisDesign.values = newValues;
        }

        if (labelItem.padding !== undefined) axisDesign.labelPadding = labelItem.padding;
        if (!isSerial) {
          if (labelItem.offset !== undefined) axisDesign.labelOffset = labelItem.offset;
          if (labelItem.anchor !== undefined) axisDesign.labelAnchor = labelItem.anchor;
          if (labelItem.angle !== undefined) axisDesign.labelAngle = labelItem.angle;
          else axisDesign.labelAngle = 0;
          // below is default setup to allow Fronto to handle specifics later (when text is replaced)
          if (["y", "y2"].includes(axis) && (labelItem.orient === "left" || labelItem.orient === undefined)) {
            axisDesign.orient = "left";
            axisDesign.labelAlign = labelItem.align || "right";
          } else if (["y", "y2"].includes(axis) && (labelItem.orient === "right" || labelItem.orient === undefined)) {
            axisDesign.orient = "right";
            axisDesign.labelAlign = "left";
          } else if (["row"].includes(axis) && (labelItem.orient === "left" || labelItem.orient === undefined)) {
            axisDesign.orient = "left";
            axisDesign.labelAlign = "left";
          } else if (["row", "y", "y2"].includes(axis) && labelItem.orient === "right") axisDesign.orient = "right";
          if (labelItem.angle === undefined && (axisDesign.orient === "top" || axisDesign.orient === "bottom")) {
            axisDesign.labelAngle = 0;
          }
        } else {
          if (layer_info.mark.type === "bar") {
            if (isSerial === "horizontal") {
              axisDesign.labelAlign = "left";
              axisDesign.labelAnchor = "middle";
              let pred_bar_height = view.height / datatype[labelItem.field]?.cardinality * 0.5;
              if (pred_bar_height > 30 && labelItem.items?.[0]?.fontSize < 14) {
                axisDesign.labelOffset = -5;
              }
              mark.yOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12) * 1.2;
              mark.height = { "band": 0.5 };
              if (encoding.column && encoding.column.header) {
                column_label_adjust_for_serialization = true;
              } else if (encoding.row && encoding.row.header) {
                row_label_adjust_for_serialization = true;
              }
            } else {
              mark.xOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12);
              labelItem.angle = -90;
              mark.width = { "band": 0.5 };
            }
          } else if (layer_info.mark.type === "rect") {
            if (isSerial === "horizontal") {
              mark.yOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12) * 0.8;
              mark.height = { "band": (encoding['y']?.field ? 1 - mark.yOffset / (view.height / datatype[encoding['y'].field].cardinality) : 0.5) * 0.7 };
              axisDesign.labelAlign = "left";
              axisDesign.labelAnchor = "middle";
            } else {
              mark.xOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12) * 2;
              mark.width = { "band": (encoding['x']?.field ? 1 - mark.xOffset / (view.width / datatype[encoding['x'].field].cardinality) : 0.5) * 0.7 };
              axisDesign.labelAlign = "right";
              axisDesign.labelAnchor = "middle";
            }
          } else if (layer_info.mark.type === "image" || layer_info.mark.type === "square") {
            if (isSerial === "horizontal") {
              mark.yOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12) * 1.2;
              axisDesign.labelAlign = "left";
              axisDesign.labelAnchor = "middle";
              axisDesign.labelPadding = 0;
            } else {
              mark.xOffset = (labelItem.anchor === "end" ? -1 : 1) * (labelItem.items?.[0]?.fontSize || 12) * 1.2;
              axisDesign.labelPadding = 0;
              axisDesign.labelAlign = "right";
              axisDesign.labelAnchor = "middle";
            }
          }
        }
        if (labelItem.items?.length > 0) {
          let maxWidth = d3.max(labelItem.items.map(h => h.width));
          if (labelItem.flex !== undefined && labelItem.flex.startsWith("horizontal")) {
            maxWidth = d3.sum(labelItem.items.map(h => h.width));
          }
          if (maxWidth !== undefined) axisDesign.labelLimit = maxWidth * 1.1;
          let fillerWidth = maxWidth;
          if (fillerWidth === undefined) {
            let textValueItems = axisDesign.values || datatype[labelItem.field].values;
            fillerWidth = d3.max(textValueItems.map(h => h.toString().length)) * 3;
          }
          if (axis === "row" || axis === "y") {
            horizontal_space = horizontal_space + fillerWidth;
          }
          let exprFiller = "!" + "@".repeat(Math.max(Math.round((fillerWidth - 20) / 5 - 3), 0));
          let axisInitial = axis[0];
          if (encoding[axis].type === "temporal" || (!encoding[axis].type && datatype[labelItem.field].type === "temporal")) {
            axisDesign.labelExpr = (axis === "row" || axis === "column" ? "'$-" + axisInitial + "-' + roundN(datum.index*10000,0) + '" + exprFiller + "'" : "'$-" + axis + "-' + roundN(datum.index*10000,0) + '" + exprFiller + "'");
            axisDesign.labelClass = (axis === "row" || axis === "column" ? "'$-" + axisInitial + "-' + roundN(datum.index*10000,0) + ' ' + datum.value + ' ' + datum.index" : "'$-" + axis + "-' + roundN(datum.index*10000,0) + ' ' + datum.value + ' ' + datum.index");
          } else {
            axisDesign.labelExpr = (axis === "row" || axis === "column" ? "'$-" + axisInitial + "-' + hashString(toStringD(datum.value)) + '" + exprFiller + "'" : "'$-" + axis + "-' + hashString(toStringD(datum.value)) + '" + exprFiller + "'");
            axisDesign.labelClass = (axis === "row" || axis === "column" ? "'$-" + axisInitial + "-' + hashString(toStringD(datum.value)) + ' ' + datum.value + ' ' + datum.index" : "'$-" + axis + "-' + hashString(toStringD(datum.value)) + ' ' + datum.value + ' ' + datum.index");
          }
          if (differentLabelTickValues) {
            axisDesign.labelExpr = `if(indexof([${labelItem.values}], datum.value) >= 0, ${axisDesign.labelExpr}, '')`;
          }
          axisDesign.labelFontSize = (labelItem.items[0].fontSize || 12);
          axisDesign.labelOpacity = 0.5;

          // register replacement!! ==> textReplacer
          let textValueItems = labelItem.values || datatype[labelItem.field].values;
          if (labelItem.values && datatype[encoding[axis].field].type === "quantitative" && encoding[axis].scale?.domain === undefined) {
            if (encoding[axis].scale === undefined) encoding[axis].scale = {};
          }
          textValueItems = textValueItems.filter(valueItem => {
            if (layer_info.transform !== undefined) {
              let filters = layer_info.transform.filter(d => d.filter.includes(labelItem.field));
              if (filters.length > 0 && filters.map(d => {
                let dobj = {};
                dobj[labelItem.field] = valueItem
                return Fronto.makeFilterFunction(d.filter)(dobj)
              }).filter(d => d == true).length != filters.length) return false;
            }
            return true;
          })
          function axisReplacementCreator(valueItem, vindex) {
            let cssName;
            let axisInitial = axis[0];
            if (encoding[axis].type === "temporal" || (!encoding[axis].type && datatype[labelItem.field].type === "temporal")) {
              cssName = (axis === "row" || axis === "column" ? "$-" + axisInitial + "-" + Fronto.round(vindex / (textValueItems.length - 1) * 10000, 0) : "$-" + axis + "-" + Fronto.round(vindex / (textValueItems.length - 1) * 10000, 0));
            } else {
              cssName = (axis === "row" || axis === "column" ? "$-" + axisInitial + "-" + Fronto.toStringD(valueItem).hashString() : "$-" + axis + "-" + Fronto.toStringD(valueItem).hashString());
            }
            let element = document.createElement("div");
            if (labelItem.angle !== undefined) element.style.transform += ' rotate(' + labelItem.angle + ')';
            if (labelItem.align !== undefined) element.style.textAlign = labelItem.align;
            if (vindex == 0 && axis == "x") element.style.textAlign = "left";
            element.className = cssName;
            if (labelItem.dx !== undefined) { element.style.transform += ' translateX(' + labelItem.dx + ')' }
            if (labelItem.dy !== undefined) { element.style.transform += ' translateY(' + labelItem.dy + ')' }
            if (labelItem.orient === "top" || axisDesign.orient === "top") {
              element.setAttribute("data-orient", "top");
            }

            if (axis === "column") { axisDesign.labelAngle = 0 }
            if (labelItem.flex !== undefined) {
              if (labelItem.flex === "horizontal-space-between") {
                element.style.display = "flex";
                element.style.justifyContent = "space-between";
              } else if (labelItem.flex === "horizontal-start") {
                element.style.display = "flex";
                element.style.justifyContent = "flex-start";
              } else if (labelItem.flex === "horizontal-end") {
                element.style.display = "flex";
                element.style.justifyContent = "flex-end";
              } else if (labelItem.flex === "horizontal-center") {
                element.style.display = "flex";
                element.style.justifyContent = "center";
              } else if (labelItem.flex === "horizontal") {
                element.style.display = "flex";
              }
            }
            if (isSerial) {
              element.setAttribute("data-serial", "serial-" + isSerial);
            }
            labelItem.items.forEach(textItem => {
              if (textItem.values !== undefined) {
                if (!textItem.values.includes(valueItem)) return;
              }
              let condEval;
              if (textItem.condition !== undefined) {
                if (textItem.condition.constructor.name === "String") {
                  let evalExpr = textItem.condition.replace(/(datum\.value)/g, "'" + valueItem + "'")
                  evalExpr = evalExpr.replace(/(datum\.index)/g, vindex / (textValueItems.length - 1))
                  let currDatum = dataset.filter(ditem => ditem[labelItem.field] === valueItem || ditem[labelItem.field] == valueItem)[0]
                  if (currDatum === undefined) currDatum = {};
                  evalExpr = evalExpr.replace(/(datum)/g, JSON.stringify(currDatum))
                  if (!evalExpr.startsWith('(')) evalExpr = "(" + evalExpr + ")";
                  condEval = Fronto.evalExpression(evalExpr);
                } else if (textItem.condition.constructor.name === "Array") {
                  condEval = textItem.condition.includes(valueItem);
                }
              }
              if (condEval === undefined || condEval === true) {
                let textContent;
                if (textItem.expression !== undefined) {
                  let currDatum = dataset.filter(ditem => ditem[labelItem.field] === valueItem || ditem[labelItem.field] == valueItem)[0]
                  if (currDatum === undefined) currDatum = {};
                  let evalExpr = textItem.expression.replace(/datum\.value/g, (typeof valueItem == "string" ? "'" + valueItem + "'" : valueItem)).replace(/(datum\.index)/g, vindex / (textValueItems.length - 1)).replace(/(datum)/g, JSON.stringify(currDatum))
                  textContent = Fronto.evalExpression(evalExpr, labelItem.field, valueItem, spec.data, true);
                } else if (textItem.custom !== undefined) {
                  textContent = textItem.custom[valueItem] ? textItem.custom[valueItem] : (textItem.format ? Fronto.evalFormat(textItem.format, valueItem) : valueItem);
                } else if (textItem.format !== undefined) {
                  let valueItem2 = (labelItem.field === "year" && datatype[labelItem.field].type === "nominal" ? valueItem + "-02-01" : valueItem);
                  textContent = Fronto.evalFormat(textItem.format, valueItem2, textItem.formatType || datatype[encoding[axis].field].type)
                } else if (textItem.value !== undefined) {
                  textContent = textItem.value;
                } else {
                  textContent = valueItem;
                }
                let textItemElement = document.createElement('div');
                textItemElement.innerHTML = textContent;
                if (textItem.width !== undefined) textItemElement.style.width = textItem.width + 'px';
                else if (labelItem.width !== undefined) textItemElement.style.width = labelItem.width + 'px';
                if (textItem.baseline !== undefined) textItemElement.style.verticalAlign = textItem.baseline;
                if (textItem.color !== undefined) textItemElement.style.color = textItem.color;
                if (textItem.fontSize !== undefined) textItemElement.style.fontSize = textItem.fontSize + 'px';
                if (textItem.fontWeight !== undefined) textItemElement.style.fontWeight = textItem.fontWeight;
                if (textItem.opacity !== undefined) textItemElement.style.opacity = textItem.opacity;
                if (textItem.fill !== undefined) textItemElement.style.backgroundColor = textItem.fill;
                if (textItem.align !== undefined) textItemElement.style.textAlign = textItem.align;
                else if (element.style.textAlign !== undefined) textItemElement.style.textAlign = element.style.textAlign;
                else textItemElement.style.textAlign = "left";
                if (textItem.lineHeight !== undefined) textItemElement.style.lineHeight = textItem.lineHeight + "%";
                if (textItem.orient !== undefined) element.setAttribute("data-orient", textItem.orient);
                element.appendChild(textItemElement);
              }
            });
            textReplacer[cssName] = { "html": element.outerHTML, "name": "F_layer_" + i + "__axisLabel_" + lai + "_" + labelItem.field };
          }
          textValueItems.forEach((valueItem, vindex) => axisReplacementCreator(valueItem, vindex))

          if (!fixedValue) {
            let axisInitial = axis[0];
            let cssName2 = "$-" + axisInitial + "-blank";
            textReplacer[cssName2] = { "creator": axisReplacementCreator };
          }
        }
        if (labelItem.title && labelItem.title !== null) {
          axisDesign.title = labelItem.field;
          axisDesign.titleClass = "$-" + axis + "-title";
          axisDesign.titleFontSize = labelItem.title.fontSize || 12;
          if (labelItem.title.width) axisDesign.titleLimit = labelItem.title.width * 1.1;
          if (labelItem.title.angle !== undefined) axisDesign.titleAngle = labelItem.title.angle;
          if (labelItem.title.padding !== undefined) axisDesign.titlePadding = labelItem.title.padding;
          if (labelItem.title.x !== undefined) axisDesign.titleX = labelItem.title.x;
          if (labelItem.title.y !== undefined) axisDesign.titleY = labelItem.title.y;
          // register replacement!! ==> textReplacer
          let cssName = "$-" + axis + "-title";
          let element = document.createElement("div");
          element.className = cssName;
          let textContent;
          if (labelItem.title.expression) {
            textContent = Fronto.evalExpression(labelItem.title.expression, labelItem.field, "", spec.data, false)
          } else if (labelItem.title.value) {
            textContent = labelItem.title.value;
          } else {
            textContent = labelItem.field;
          }
          let textItemElement = document.createElement('div');
          textItemElement.innerHTML = textContent;
          if (labelItem.title.align) textItemElement.style.textAlign = labelItem.title.align;
          if (labelItem.title.width) textItemElement.style.width = labelItem.title.width + 'px';
          if (labelItem.title.angle) textItemElement.style.transform += ' rotate(' + labelItem.title.angle + ')';
          if (labelItem.title.baseline) textItemElement.style.verticalAlign = labelItem.title.baseline;
          if (labelItem.title.color) textItemElement.style.color = labelItem.title.color;
          if (labelItem.title.fontSize) textItemElement.style.fontSize = labelItem.title.fontSize + 'px';
          if (labelItem.title.fontWeight) textItemElement.style.fontWeight = labelItem.title.fontWeight;
          if (labelItem.title.opacity) textItemElement.style.opacity = labelItem.title.opacity;
          element.appendChild(textItemElement);
          textReplacer[cssName] = { "html": element.outerHTML, "name": "F_layer_" + i + "__axisLabelTitle_" + lai + "_" + labelItem.field };
        }
        if (labelItem.tick && !["row", "column"].includes(axis)) { // tick is not available for row/columns
          if (labelItem.tick === true) {
            axisDesign.ticks = true;
          } else if (labelItem.tick.constructor.name === "Object") {
            axisDesign.ticks = true;
            if (labelItem.tick.color) axisDesign.tickColor = labelItem.tick.color;
            if (labelItem.tick.opacity) axisDesign.tickOpacity = labelItem.tick.opacity;
            if (labelItem.tick.size) axisDesign.tickSize = labelItem.tick.size;
            if (labelItem.tick.width) axisDesign.tickWidth = labelItem.tick.width;
            if (labelItem.tick.offset) axisDesign.offset = labelItem.tick.offset;
            if (labelItem.tick.dash) axisDesign.tickDash = labelItem.tick.dash;
          }
        }
      })
    });
    if (column_label_adjust_for_serialization && encoding.column?.header) {
      encoding.column.header.labelPadding = (encoding.column.header.labelPadding || 0) - 20;
    }
    if (row_label_adjust_for_serialization && encoding.row?.header && encoding.row.header.orient === "top") {
      encoding.row.header.labelPadding = (encoding.row.header.labelPadding || 0) - 20;
    }
    // legend labels
    label_legend?.forEach((labelItem, lai) => {
      let targetColumns = Object.keys(encoding).filter(channel => {
        return !["x", "y", "x2", "y2", "row", "column"].includes(channel) &&
          (Fronto.simpleNotUndefinedEq(encoding[channel].field, labelItem.field) || Fronto.simpleNotUndefinedEq(encoding[channel].aggregate === labelItem.aggregate))
          && !lineEndMark_adjust.includes(channel);
      })
      let ignoreVLpos = labelItem.ignoreVLpos || false;
      targetColumns.forEach(channel => { // axis labels and ticks
        let legendConfig = {};
        if (encoding[channel].legend === undefined || encoding[channel].legend === null) encoding[channel].legend = {};
        let legendDesign = encoding[channel].legend;
        legendDesign.legend = true;
        if (labelItem.direction !== undefined) legendDesign.direction = labelItem.direction;
        if (labelItem.orient !== undefined) legendDesign.orient = labelItem.orient;
        if (labelItem.values !== undefined) legendDesign.values = labelItem.values;
        else legendDesign.values = Fronto.getReferenceDefaultValues(datatype[labelItem.field].values);
        let valueNCols = spec.layout?.width / 120;
        if (valueNCols) {
          legendDesign.columns = Math.round(valueNCols);
        }
        if (labelItem.offset !== undefined) legendDesign.offset = labelItem.offset;
        if (labelItem.anchor !== undefined) legendDesign.labelAnchor = labelItem.anchor;
        if (labelItem.angle !== undefined) legendDesign.labelAngle = labelItem.angle;
        if (labelItem.width !== undefined) legendDesign.labelLimit = 60;
        if (labelItem.dx !== undefined) legendConfig.legendX = labelItem.dx;
        if (labelItem.dy !== undefined) legendConfig.legendY = labelItem.dy;
        if (labelItem.symbol !== undefined) {
          Object.keys(labelItem.symbol).forEach(symbolProp => {
            legendConfig["symbol" + symbolProp.capitalize()] = labelItem.symbol[symbolProp];
          })
        }
        if (labelItem.items?.length > 0) {
          legendDesign.labelExpr = "'!l-' + hashString(toStringD(datum.value))";
          legendDesign.labelFontSize = labelItem.items[0].fontSize || 12;
          let maxWidth = d3.max(labelItem.items.map(h => h.width));
          // register replacement!! ==> textReplacer
          let textValueItems = legendDesign.values;
          textValueItems.forEach((valueItem, vindex) => {
            let cssName = "!l-" + Fronto.toStringD(valueItem).hashString();
            let element = document.createElement("div");
            if (labelItem.angle !== undefined) element.style.transform += ' rotate(' + labelItem.angle + ')';
            if (labelItem.align !== undefined) element.style.textAlign = labelItem.align;
            if (ignoreVLpos) element.setAttribute("data-ignroe-vl-position", labelItem.orient || "top");
            element.className = cssName;
            labelItem.items.forEach(textItem => {
              let textContent;
              if (textItem.expression !== undefined) {
                let evalExpr = textItem.expression.replace(/datum\.value/g, (typeof valueItem == "string" ? "'" + valueItem + "'" : valueItem))
                evalExpr = evalExpr.replace(/(datum\.index)/g, vindex / (textValueItems.length - 1));
                textContent = Fronto.evalExpression(evalExpr, labelItem.field, valueItem, spec.data, true)
              } else if (textItem.custom !== undefined) {
                textContent = textItem.custom[valueItem] ? textItem.custom[valueItem] : (textItem.format ? Fronto.evalFormat(textItem.format, valueItem) : valueItem);
              } else if (textItem.format !== undefined) {
                textContent = Fronto.evalFormat(textItem.format, valueItem)
              } else {
                textContent = valueItem;
              }
              let textItemElement = document.createElement('div');
              textItemElement.innerHTML = textContent;
              if (textItem.width !== undefined) textItemElement.style.width = Fronto.unitString(textItem.width, 'px');
              if (textItem.lineHeight !== undefined) textItemElement.style.lineHeight = Fronto.unitString(textItem.lineHeight, "%");
              else textItemElement.style.lineHeight = "100%";
              if (textItem.baseline !== undefined) textItemElement.style.verticalAlign = textItem.baseline;
              if (textItem.color !== undefined) textItemElement.style.color = textItem.color;
              if (textItem.fontSize !== undefined) textItemElement.style.fontSize = Fronto.unitString(textItem.fontSize, 'px');
              if (textItem.fontWeight !== undefined) textItemElement.style.fontWeight = textItem.fontWeight;
              if (textItem.opacity !== undefined) textItemElement.style.opacity = textItem.opacity;
              textItemElement.style.marginBottom = Fronto.unitString(textItem.marginBottom || 0, "px");
              textItemElement.style.marginTop = Fronto.unitString(textItem.marginTop || 0, "px");
              textItemElement.style.marginLeft = Fronto.unitString(textItem.marginLeft || 0, "px");
              textItemElement.style.marginRight = Fronto.unitString(textItem.marginRight || 0, "px");
              element.appendChild(textItemElement);
            });
            if (labelItem.gap !== undefined) {
              element.setAttribute("data-gap", labelItem.gap + "px");
            }
            textReplacer[cssName] = { "html": element.outerHTML, "name": "F_layer_" + i + "__legend_" + lai + "_" + labelItem.field + "_" + channel };
            if (labelItem.interactiveHighlight) {
              let hlcssname = "f-legend-hl-" + spec.name + "-" + "layer-" + i + "-legend-" + lai + "-" + labelItem.field + "-" + channel;
              textReplacer[cssName].event = {
                "on": {
                  type: labelItem.interactiveHighlight,
                  function: (event) => {
                    let marks = Array.from(document.querySelectorAll(`#view-${spec.name} g.role-mark`)).filter((g) => (g.className.baseVal.includes(' F_layer_' + i)))[0];
                    Array.from(marks?.children || []).forEach((m) => {
                      if (m.getAttribute('aria-label').includes(`${labelItem.field}: ${valueItem}`)) {
                        m.style.opacity = 1;
                      } else {
                        m.style.opacity = 0.2;
                      }
                      m.classList.add(hlcssname);
                    });
                  }
                },
                "off": {
                  type: labelItem.interactiveHighlight === "mouseover" ? "mouseout" : labelItem.interactiveHighlight,
                  function: (event) => {
                    let obs = document.querySelectorAll('.' + hlcssname);
                    Array.from(obs).forEach((m) => {
                      m.style.opacity = 1;
                      m.classList.remove(hlcssname);
                    });
                  }
                }
              }
            }
          })
        }
        if (labelItem.title && labelItem.title !== null) {
          legendDesign.title = "!l-" + channel + "-title";
          legendDesign.titleFontSize = labelItem.title.fontSize || 12;
          if (labelItem.title.width) legendDesign.titleLimit = labelItem.title.width * 1.1;
          if (labelItem.title.angle !== undefined) legendDesign.titleAngle = labelItem.title.angle;
          if (labelItem.title.padding !== undefined) legendDesign.titlePadding = labelItem.title.padding;
          // register replacement!! ==> textReplacer
          let cssName = "!l-" + channel + "-title";
          let element = document.createElement("div");
          element.className = cssName;
          let textContent;
          if (labelItem.title.expression) {
            textContent = Fronto.evalExpression(labelItem.title.expression, labelItem.field, "", spec.data, false)
          } else if (labelItem.title.value) {
            textContent = labelItem.title.value;
          } else {
            textContent = labelItem.field;
          }
          let textItemElement = document.createElement('div');
          textItemElement.innerHTML = textContent;
          if (labelItem.title.align) textItemElement.style.textAlign = labelItem.title.align;
          if (labelItem.title.width) textItemElement.style.width = Fronto.unitString(labelItem.title.width, 'px');
          if (labelItem.title.angle) textItemElement.style.transform += ' rotate(' + labelItem.title.angle + ')';
          if (labelItem.title.baseline) textItemElement.style.verticalAlign = labelItem.title.baseline;
          if (labelItem.title.color) textItemElement.style.color = labelItem.title.color;
          if (labelItem.title.fontSize) textItemElement.style.fontSize = Fronto.unitString(labelItem.title.fontSize, 'px');
          if (labelItem.title.fontWeight) textItemElement.style.fontWeight = labelItem.title.fontWeight;
          if (labelItem.title.opacity) textItemElement.style.opacity = labelItem.title.opacity;
          textItemElement.style.marginBottom = Fronto.unitString(labelItem.title.marginBottom || 5, "px");
          textItemElement.style.marginTop = Fronto.unitString(labelItem.title.marginTop || 0, "px");
          textItemElement.style.marginLeft = Fronto.unitString(labelItem.title.marginLeft || 0, "px");
          textItemElement.style.marginRight = Fronto.unitString(labelItem.title.marginRight || 0, "px");
          element.appendChild(textItemElement);
          textReplacer[cssName] = { "html": element.outerHTML, "name": "F_layer_" + i + "__legendTitle_" + lai + "_" + labelItem.field + "_" + channel };
        } else {
          legendConfig.title = null;
        }
        if (Object.keys(legendConfig).length > 0) {
          if (view.config === undefined) view.config = {};
          view.config.legend = legendConfig;
        }
      })
    });

    // on-mark labels
    label_mark?.forEach((labelItem, lii) => {
      // register nondata annotations;
      let field = labelItem.field;
      let countAgg = labelItem.aggregate === "count";
      let values = labelItem.values || spec.data.map(d => d[field]);
      if (!['line', 'area'].includes(mark.type)) {
        // not line/area
        let isSerial = false;
        if (labelItem.serial === true && ["bar", "rect"].includes(layer_info.mark?.type)) {
          if (encoding.x.type === encoding.y.type) isSerial = "vertical";
          else {
            if (encoding.y.type === "quantitative") isSerial = "vertical";
            else if (encoding.y.type === "nominal") isSerial = "horizontal";
            else if (encoding.x.type === "quantitative") isSerial = "horizontal";
            else if (encoding.x.type === "nominal") isSerial = "vertical";
            else if (encoding.x.type === "ordinal" && encoding.y.type === "temporal") isSerial = "horizontal";
            else if (encoding.x.type === "temporal" && encoding.y.type === "ordinal") isSerial = "vertical";
          }
        }
        let labelData = spec.data;
        if (labelItem.aggregate) {
          let agg_method = labelItem.aggregate;
          let groupby = [];
          Object.keys(encoding).forEach(channel => {
            if (encoding[channel].field && (encoding[channel].type === "nominal" || encoding[channel].type === "ordinal")) {
              groupby.push(encoding[channel].field);
            }
          })
          labelData = Fronto.aggregate_data(labelData, field, agg_method, groupby);
        }
        labelData.forEach((datum, index) => {
          let filters = layer_info.transform || [];
          if (filters.length > 0 && filters.map(d => {
            let x = Fronto.makeFilterFunction(d.filter)(datum);
            return x;
          }).filter(d => d == true).length != filters.length) return;
          let fieldname = (countAgg ? "count" : (labelItem.aggregate ? labelItem.aggregate + "_" + field : field));
          if (values.includes(datum[fieldname])) { // if it designated, 
            let annoItem = {
              "name": "on-mark-anno-" + field + "-" + index
            }; // annotation item
            if (labelItem.visibility?.externalize !== undefined) {
              annoItem.externalize = Fronto.deepcopy(labelItem.visibility.externalize);
              if (annoItem.externalize.numbering == true) {
                annoItem.externalize.numbering = "yes";
                mark_label_numbering_count++;
              }
              annoItem.externalize.type = "yes";
            } else {
            }
            annoItem.anchor = {
              "textAnchor": true,
              "dataItem": {
                "role": layer_info.mark.type,
                "datum": datum
              }
            };
            annoItem.box = {};
            // box styling
            if (labelItem.dx !== undefined) annoItem.dx = labelItem.dx;
            if (labelItem.dy !== undefined) annoItem.dy = labelItem.dy;
            if (labelItem.anchorAlign !== undefined) annoItem.anchor.anchorAlign = labelItem.anchorAlign;
            if (!isSerial) {
              if (labelItem.orient !== undefined) annoItem.anchor.positionOrient = labelItem.orient;
            } else {
              let orientSplit = labelItem.orient.split("-");
              if (isSerial === "horizontal") {
                if (orientSplit[0] === "middle") annoItem.anchor.positionOrient = "top-" + (orientSplit[1] || 'left');
                else if (labelItem.orient !== undefined) annoItem.anchor.positionOrient = labelItem.orient;
                if (orientSplit[0] === "bottom") annoItem.dy = "100%";
                else annoItem.dy = "-100%";
              } else if (isSerial === "vertical") {
                if (labelItem.anchorAlign === "center") annoItem.anchor.positionOrient = (orientSplit[0] || 'bottom') + "-left";
                else if (labelItem.orient !== undefined) annoItem.anchor.positionOrient = labelItem.orient;
              }
            }
            // callout
            if (labelItem.tick == true || (labelItem.tick?.constructor.name === "Object" && labelItem.tick.ticks == true)) {
              annoItem.anchor.callout = "yes";
              if (labelItem.tick?.offset) annoItem.anchor.padding = labelItem.tick.offset;
              if (labelItem.tick?.color) annoItem.anchor.strokeColor = labelItem.tick.color;
              if (labelItem.tick?.opacity) annoItem.anchor.strokeOpacity = labelItem.tick.opacity;
              if (labelItem.tick?.width) annoItem.anchor.strokeWidth = labelItem.tick.width;
              if (labelItem.tick?.dash) annoItem.anchor.strokeDash = labelItem.tick.dash;
              if (labelItem.tick?.from) annoItem.anchor.from = labelItem.tick.from;
              if (labelItem.tick?.to) annoItem.anchor.from = labelItem.tick.to;
            } else {
              annoItem.anchor.callout = "no"
            }
            if (labelItem.opacity !== undefined) annoItem.opacity = labelItem.opacity;
            let widths = labelItem.items?.map(te => te.width).filter(te => te !== undefined);
            if (labelItem.width !== undefined) annoItem.width = labelItem.width;
            else if (widths.length > 0) annoItem.width = widths.f$max();
            if (labelItem.height !== undefined) annoItem.height = labelItem.height;
            if (labelItem.padding !== undefined) annoItem.box.padding = labelItem.padding;
            if (labelItem.paddingTop !== undefined) annoItem.box.paddingTop = labelItem.paddingTop;
            if (labelItem.paddingBottom !== undefined) annoItem.box.paddingBottom = labelItem.paddingBottom;
            if (labelItem.paddingLeft !== undefined) annoItem.box.paddingLeft = labelItem.paddingLeft;
            if (labelItem.paddingRight !== undefined) annoItem.box.paddingRight = labelItem.paddingRight;
            if (labelItem.radius !== undefined) annoItem.box.rounded = labelItem.radius;
            if (labelItem.fill !== undefined) annoItem.box.fill = labelItem.fill;
            if (labelItem.fillOpacity !== undefined) annoItem.box.opacity = labelItem.fillOpacity;
            if (labelItem.stroke !== undefined) annoItem.box.borderColor = labelItem.stroke;
            if (labelItem.strokeOpacity !== undefined) annoItem.box.borderOpacity = labelItem.strokeOpacity;
            if (labelItem.strokeWidth !== undefined) annoItem.box.borderWidth = labelItem.strokeWidth;
            if (labelItem.strokeStyle !== undefined) annoItem.box.borderStyle = labelItem.strokeStyle;
            if (labelItem.angle !== undefined) annoItem.rotate = labelItem.angle;
            if (labelItem.align) annoItem.text.align = labelItem.align;
            else if (labelItem.textAlign) annoItem.text.align = labelItem.textAlign;

            // conditioning
            if (labelItem.conditions?.length > 0) {
              labelItem.conditions.forEach(cond => {
                let condEval = false;
                if (cond.datum) {
                  condEval = Object.keys(cond.datum).every(datumKey => {
                    return cond.datum[datumKey] == datum[datumKey]
                  })
                }
                else if (cond.condition) {
                  condEval = eval(cond.condition)
                }
                if (condEval) {
                  Object.keys(cond.options).forEach(optKey => {
                    annoItem[optKey] = Fronto.setObjectValue(annoItem[optKey], cond.options[optKey]);
                  });
                }
              })
            }
            let textContent = "";
            let fieldValue = datum[fieldname];
            if (labelItem.items?.length > 0) {
              labelItem.items.forEach(textItem => {
                let condEval;
                if (textItem.condition !== undefined) {
                  if (textItem.condition.constructor.name === "String") {
                    let evalExpr = textItem.condition.replace(/(datum\.value)/g, "'" + datum[fieldname] + "'")
                    evalExpr = evalExpr.replace(/(datum)/g, JSON.stringify(datum))
                    if (!evalExpr.startsWith('(')) evalExpr = "(" + evalExpr + ")";
                    condEval = Fronto.evalExpression(evalExpr);
                  } else if (textItem.condition.constructor.name === "Array") {
                    condEval = textItem.condition.includes(datum[fieldname]);
                  }
                }
                if (condEval === undefined || condEval === true) {
                  let textDom = document.createElement("div");
                  let textValue;
                  if (textItem.expression) {
                    let newValue = fieldValue;
                    if (textItem.format) newValue = d3.format(textItem.format)(fieldValue);
                    let newExpr = textItem.expression.replace(/(datum\.value)/g, "'" + newValue + "'").replace(/(datum)/g, JSON.stringify(datum));
                    textValue = Fronto.evalExpression(newExpr, field, fieldValue, spec.data, true, {
                      "datatype": datatype
                    });
                  } else if (textItem.custom) {
                    if (textItem.format) textValue = textItem.custom[fieldValue] ? textItem.custom[fieldValue] : d3.format(textItem.format)(fieldValue);
                    else textValue = textItem.custom[fieldValue] ? textItem.custom[fieldValue] : fieldValue;
                  } else if (textItem.format) {
                    textValue = d3.format(textItem.format)(fieldValue)
                  } else if (textItem.content) {
                    textValue = textItem.content;
                  } else {
                    textValue = fieldValue;
                  }

                  if (labelItem.anchorAlign === "end") {
                    textDom.style.marginRight = 0;
                    textDom.style.marginLeft = "auto";
                  }
                  textDom.innerHTML = textValue;
                  if (textItem.fontSize) textDom.style.fontSize = Fronto.unitString(textItem.fontSize, 'px');
                  if (textItem.width !== undefined) textDom.style.width = Fronto.unitString(textItem.width, "px");
                  if (textItem.color === "$mark") {
                    textDom.setAttribute("data-text-color", "$mark");
                  } if (textItem.color === "$black-white") {
                    textDom.setAttribute("data-text-color", "$black-white");
                  } else if (textItem.color) textDom.style.color = textItem.color;
                  if (textItem.fontStroke === "$mark") {
                    textDom.setAttribute("data-text-fontStroke", "$mark");
                  } if (textItem.fontStroke === "$black-white") {
                    textDom.setAttribute("data-text-fontStroke", "$black-white");
                  } else if (textItem.fontStroke) textDom.style.textShadow = Fronto.makeTextShadow(textItem.fontStroke);
                  if (textItem.fontWeight) textDom.style.fontWeight = textItem.fontWeight;
                  if (textItem.opacity) textDom.style.opacity = textItem.opacity;
                  if (textItem.padding) textDom.style.padding = textItem.padding;
                  if (textItem.align) textDom.style.textAlign = textItem.align;
                  else if (textItem.textAlign) textDom.style.textAlign = textItem.textAlign;
                  textContent += textDom.outerHTML;
                }
              });
            }
            annoItem.text = {
              "value": textContent
            }
            if (labelItem.anchorAlign === "end") annoItem.text.align = "right";
            annoItem.className = "F_layer_" + i + "__markLabel_" + lii + "_" + field;
            annotation.data.items.push(annoItem);
          }
        });
      } else {
        // line / area
        if (labelItem.items?.length > 0) {
          if (labelItem.visibility?.externalize != false && labelItem.visibility?.externalize !== undefined) { // external
            let isNumbering = labelItem.visibility.externalize.numbering == true;
            // external 
            let $sublayer_linemark_text = {
              "name": "F_layer_" + i + "__markLabel_" + lii + "_" + field,
              "mark": { "type": "text", "align": "left", "limit": 15 },
              "transform": [{
                "calculate": "'$-t" + labelItem.field[0] + lii + "-'+replace(datum." + labelItem.field + ",/[\\s&@#$!\\^\\*\\(\\)\\+\\=\\?\\/\\[\\]]/gi,'_')", "as": labelItem.field + "_repl"
              }],
              "encoding": {}
            };
            if (line_direction === "horizontal") {
              $sublayer_linemark_text.encoding = {
                "text": { "field": labelItem.field + "_repl", "type": "nominal" },
                "textClass": { "field": labelItem.field + "_repl", "type": "nominal" },
                "x": { "aggregate": "max", "field": encoding.x.field, "type": encoding.x.type },
                "y": { "aggregate": { "argmax": encoding.x.field }, "field": encoding.y.field, "type": encoding.y.type }
              };
            } else {
              $sublayer_linemark_text.encoding = {
                "text": { "field": labelItem.field + "_repl", "type": "nominal" },
                "textClass": { "field": labelItem.field + "_repl", "type": "nominal" },
                "x": { "aggregate": { "argmax": encoding.y.field }, "field": encoding.x.field, "type": encoding.x.type },
                "y": { "aggregate": "max", "field": encoding.y.field, "type": encoding.y.type }
              };
            }
            if (labelItem.values) {
              $sublayer_linemark_text.transform.push({
                "filter": "includes(" + JSON.stringify(labelItem.values) + ", datum." + labelItem.field + ")"
              })
            }
            if (layer_info.transform) {
              $sublayer_linemark_text.transform.push(...layer_info.transform)
            }

            datatype[labelItem.field].values.forEach((valueItem, vi) => { // numbering
              if (labelItem.values && !labelItem.values?.includes(valueItem)) return;
              let filters = $sublayer_linemark_text.transform.filter(d => d.filter?.includes(labelItem.field))
              if (filters.length > 0 && filters.map(d => {
                let dobj = {};
                dobj[labelItem.field] = valueItem
                return Fronto.makeFilterFunction(d.filter)(dobj)
              }).filter(d => d == true).length != filters.length) return;
              let cssName = "$-t" + labelItem.field[0] + lii + "-" + valueItem.replace(/[\s&@#$!\^\*\(\)\+\=\?\/\[\]]/gi, "_");
              let element = document.createElement("div");
              if (labelItem.angle !== undefined) element.style.transform += ' rotate(' + labelItem.angle + ')';
              if (labelItem.align !== undefined) element.style.textAlign = labelItem.align;
              if (labelItem.dx !== undefined) {
                $sublayer_linemark_text.mark.dx = labelItem.dx;
              }
              if (labelItem.dy !== undefined) {
                $sublayer_linemark_text.mark.dy = labelItem.dy;
              }
              if (isNumbering) element.setAttribute("data-numbering", "true");
              element.className = cssName;
              let innerElement = document.createElement("div");
              innerElement.innerText = mark_label_numbering_count + 1;
              // color
              if (labelItem.items?.length == 1) {
                let textItem = labelItem.items[0];
                if (textItem.color === "$mark") innerElement.setAttribute("data-text-color", "$mark");
                else if (textItem.color !== undefined) innerElement.style.color = textItem.color;
                if (textItem.fontStroke === "$mark") innerElement.setAttribute("data-text-fontStroke", "$mark");
                else if (textItem.fontStroke !== undefined) innerElement.style.textShadow = Fronto.makeTextShadow(textItem.fontStroke);
                if (textItem.fontSize !== undefined) innerElement.style.fontSize = textItem.fontSize + 'px';
                if (textItem.fontWeight !== undefined) innerElement.style.fontWeight = textItem.fontWeight;
                if (textItem.opacity !== undefined) innerElement.style.opacity = textItem.opacity;
                if (textItem.fill !== undefined) innerElement.style.backgroundColor = textItem.fill;
              }
              element.innerHTML = innerElement.outerHTML;
              // external item 
              let externalAnnoItem = {
                "name": "on-mark-anno-" + labelItem.field + "-" + vi
              };
              if (labelItem.visibility?.externalize !== undefined) {
                externalAnnoItem.externalize = Fronto.deepcopy(labelItem.visibility.externalize);
                if (externalAnnoItem.externalize.numbering == true) {
                  externalAnnoItem.externalize.numbering = "hidden";
                  externalAnnoItem.externalize.number_count = (mark_label_numbering_count + 1);
                }
                externalAnnoItem.externalize.type = "yes";
              }
              let textDom = document.createElement("div");

              labelItem.items.forEach(textItem => {
                if (textItem.condition?.constructor.name === "Array" && !textItem.condition?.includes(valueItem)) return;
                if (textItem.values?.constructor.name === "Array" && !textItem.values.includes(valueItem)) return;
                let textContent;
                if (textItem.expression !== undefined) {
                  let newExpr = textItem.expression.replace(/(datum\.value)/g, "'" + valueItem + "'");
                  textContent = Fronto.evalExpression(newExpr, labelItem.field, valueItem, spec.data, true);
                } else if (textItem.custom !== undefined) {
                  textContent = textItem.custom[valueItem] ? textItem.custom[valueItem] : (textItem.format ? Fronto.evalFormat(textItem.format, valueItem) : valueItem);
                } else if (textItem.format !== undefined) {
                  textContent = Fronto.evalFormat(textItem.format, valueItem, textItem.formatType || datatype[labelItem.field].type)
                } else if (textItem.content) {
                  textContent = textItem.content;
                } else {
                  textContent = valueItem;
                }
                let textItemElement = document.createElement('div');
                textItemElement.innerHTML = textContent;
                if (textItem.baseline !== undefined) textItemElement.style.verticalAlign = textItem.baseline;
                if (textItem.color === "$mark") textItemElement.setAttribute("data-text-color", "$mark");
                else if (textItem.color !== undefined) textItemElement.style.color = textItem.color;
                if (textItem.fontStroke === "$mark") textItemElement.setAttribute("data-text-fontStroke", "$mark");
                else if (textItem.fontStroke !== undefined) textItemElement.style.textShadow = Fronto.makeTextShadow(textItem.fontStroke);
                if (textItem.fontSize !== undefined) textItemElement.style.fontSize = textItem.fontSize + 'px';
                if (textItem.fontWeight !== undefined) textItemElement.style.fontWeight = textItem.fontWeight;
                if (textItem.opacity !== undefined) textItemElement.style.opacity = textItem.opacity;
                if (textItem.fill !== undefined) textItemElement.style.backgroundColor = textItem.fill;
                if (textItem.align !== undefined) { textItemElement.style.textAlign = textItem.align; }
                else if (textItem.textAlign !== undefined) { textItemElement.style.textAlign = textItem.textAlign; }
                textDom.appendChild(textItemElement);
              });
              externalAnnoItem.text = { value: textDom.outerHTML };
              annotation.data.items.push(externalAnnoItem);
              if (isNumbering) {
                if (labelItem.tick == true || (labelItem.tick?.constructor.name === "Object" && labelItem.tick.ticks == true)) {
                  element.setAttribute("data-callout", "yes");
                  let vobj = {};
                  vobj[labelItem.field] = valueItem;
                  element.setAttribute("data-datum", JSON.stringify(vobj));
                  element.setAttribute("data-mark", mark.type);
                  element.setAttribute("data-viewname", view.name);
                  if (labelItem.tick?.offset !== undefined) element.setAttribute("data-callout-offset", labelItem.tick.offset);
                  if (labelItem.tick?.color !== undefined) element.setAttribute("data-callout-color", labelItem.tick.color);
                  if (labelItem.tick?.opacity !== undefined) element.setAttribute("data-callout-opacity", labelItem.tick.opacity);
                  if (labelItem.tick?.width !== undefined) element.setAttribute("data-callout-width", labelItem.tick.width);
                  if (labelItem.tick?.dash !== undefined) element.setAttribute("data-callout-dash", labelItem.tick.dash.join(","));
                  if (labelItem.tick?.width !== undefined) element.setAttribute("data-callout-width", labelItem.tick.width);
                  if (labelItem.tick?.from?.dx !== undefined) element.setAttribute("data-callout-from-dx", labelItem.tick.from.dx);
                  if (labelItem.tick?.from?.dy !== undefined) element.setAttribute("data-callout-from-dy", labelItem.tick.from.dy);
                  if (labelItem.tick?.to?.dx !== undefined) element.setAttribute("data-callout-to-dx", labelItem.tick.to.dx);
                  if (labelItem.tick?.to?.dy !== undefined) element.setAttribute("data-callout-to-dy", labelItem.tick.to.dy);
                } else {
                  element.setAttribute("data-callout", "no");
                }
                textReplacer[cssName] = { "html": element.outerHTML };
              }
              mark_label_numbering_count++;
            });
            if (isNumbering) { // numbering
              if ($sublayer_linemark_text !== undefined) {
                sublayer_linemark_text.push($sublayer_linemark_text)
              }
            }
          } else {
            // internal
            let $sublayer_linemark_text = {
              "name": "F_layer_" + i + "__markLabel_" + lii + "_" + field,
              "mark": { "type": "text", "align": "left", "dx": 4 },
              "transform": [{
                "calculate": "'$-t" + labelItem.field[0] + lii + "-'+replace(datum." + labelItem.field + ",/\\s/gi,'_')", "as": labelItem.field + "_repl"
              }],
              "encoding": {}
            };
            if (line_direction === "horizontal") {
              $sublayer_linemark_text.encoding = {
                "text": { "field": labelItem.field + "_repl", "type": "nominal" },
                "textClass": { "field": labelItem.field + "_repl", "type": "nominal" },
                "x": { "aggregate": "max", "field": encoding.x.field, "type": encoding.x.type },
                "y": { "aggregate": { "argmax": encoding.x.field }, "field": encoding.y.field, "type": encoding.y.type }
              };
            } else {
              $sublayer_linemark_text.encoding = {
                "text": { "field": labelItem.field + "_repl", "type": "nominal" },
                "textClass": { "field": labelItem.field + "_repl", "type": "nominal" },
                "x": { "aggregate": { "argmax": encoding.y.field }, "field": encoding.x.field, "type": encoding.x.type },
                "y": { "aggregate": "max", "field": encoding.y.field, "type": encoding.y.type }
              };
            }
            if (x_bin !== null) {
              $sublayer_linemark_text.transform.push({
                "filter": "datum." + x_bin.field + " >= " + (x_bin.stop - x_bin.step)
              });
              delete $sublayer_linemark_text.encoding.x;
              delete $sublayer_linemark_text.encoding.y;
            }
            if (labelItem.values) {
              $sublayer_linemark_text.transform.push({
                "filter": "includes(" + JSON.stringify(labelItem.values) + ", datum." + labelItem.field + ")"
              })
            }
            if (layer_info.transform) {
              $sublayer_linemark_text.transform.push(...layer_info.transform)
            }
            if (labelItem.angle !== undefined) {
              $sublayer_linemark_text.encoding.angle = { "value": labelItem.angle }
            }

            let max_width = Math.max(...(labelItem.items?.map(d => d.width) || []));
            if (max_width) $sublayer_linemark_text.mark.limit = max_width;
            datatype[labelItem.field].values.forEach(valueItem => {
              if (labelItem.values && !labelItem.values?.includes(valueItem)) return;
              let filters = $sublayer_linemark_text.transform.filter(d => d.filter?.includes(labelItem.field))
              if (filters.length > 0 && filters.map(d => {
                let dobj = {};
                dobj[labelItem.field] = valueItem;
                return Fronto.makeFilterFunction(d.filter)(dobj)
              }).filter(d => d == true).length != filters.length) return;
              let cssName = "$-t" + labelItem.field[0] + lii + "-" + valueItem.toString().replace(/\s/gi, '_');
              let element = document.createElement("div");
              if (labelItem.align !== undefined) { element.style.textAlign = labelItem.align; }
              else if (labelItem.textAlign !== undefined) { element.style.textAlign = labelItem.textAlign; }
              if (labelItem.dx !== undefined) {
                $sublayer_linemark_text.mark.dx = labelItem.dx;
              }
              if (labelItem.dy !== undefined) {
                $sublayer_linemark_text.mark.dy = labelItem.dy;
              }
              element.className = cssName;
              labelItem.items.forEach(textItem => {
                if (textItem.condition?.constructor.name === "Array" && !textItem.condition?.includes(valueItem)) return;
                if (textItem.values?.constructor.name === "Array" && !textItem.values.includes(valueItem)) return;
                let textContent;
                if (textItem.expression !== undefined) {
                  let newExpr = textItem.expression.replace(/(datum\.value)/g, "'" + valueItem + "'");
                  textContent = Fronto.evalExpression(newExpr, labelItem.field, valueItem, spec.data, true);
                } else if (textItem.custom !== undefined) {
                  textContent = textItem.custom[valueItem] ? textItem.custom[valueItem] : (textItem.format ? Fronto.evalFormat(textItem.format, valueItem) : valueItem);
                } else if (textItem.format !== undefined) {
                  textContent = Fronto.evalFormat(textItem.format, valueItem, (textItem.formatType || datatype[labelItem.field].type))
                } else if (textItem.content) {
                  textContent = textItem.content;
                } else {
                  textContent = valueItem;
                }
                let textItemElement = document.createElement('div');
                textItemElement.innerHTML = textContent;
                if (textItem.width !== undefined) textItemElement.style.width = textItem.width + 'px';
                if (textItem.baseline !== undefined) textItemElement.style.verticalAlign = textItem.baseline;
                if (textItem.color === "$mark") textItemElement.setAttribute("data-text-color", "$mark");
                else if (textItem.color !== undefined) textItemElement.style.color = textItem.color;
                if (textItem.fontStroke === "$mark") textItemElement.setAttribute("data-text-fontStroke", "$mark");
                else if (textItem.fontStroke !== undefined) textItemElement.style.textShadow = Fronto.makeTextShadow(textItem.fontStroke);
                if (textItem.fontSize !== undefined) textItemElement.style.fontSize = textItem.fontSize + 'px';
                if (textItem.fontWeight !== undefined) textItemElement.style.fontWeight = textItem.fontWeight;
                if (textItem.opacity !== undefined) textItemElement.style.opacity = textItem.opacity;
                if (textItem.fill !== undefined) textItemElement.style.backgroundColor = textItem.fill;
                if (textItem.lineHeight !== undefined) textItemElement.style.lineHeight = textItem.lineHeight;
                else textItemElement.style.lineHeight = 1;
                if (textItem.align !== undefined) { textItemElement.style.textAlign = textItem.align; }
                else if (textItem.textAlign !== undefined) { textItemElement.style.textAlign = textItem.textAlign; }
                element.appendChild(textItemElement);
              });
              if (labelItem.tick == true || (labelItem.tick?.constructor.name === "Object" && labelItem.tick.ticks == true)) {
                element.setAttribute("data-callout", "yes");
                let vobj = {};
                vobj[labelItem.field] = valueItem;
                element.setAttribute("data-datum", JSON.stringify(vobj));
                element.setAttribute("data-mark", mark.type);
                element.setAttribute("data-viewname", view.name);
                if (labelItem.tick?.offset) element.setAttribute("data-callout-offset", labelItem.tick.offset);
                if (labelItem.tick?.color) element.setAttribute("data-callout-color", labelItem.tick.color);
                if (labelItem.tick?.opacity) element.setAttribute("data-callout-opacity", labelItem.tick.opacity);
                if (labelItem.tick?.width) element.setAttribute("data-callout-width", labelItem.tick.width);
                if (labelItem.tick?.dash) element.setAttribute("data-callout-dash", labelItem.tick.dash.join(","));
                if (labelItem.tick?.from?.dx !== undefined) element.setAttribute("data-callout-from-dx", labelItem.tick.from.dx);
                if (labelItem.tick?.from?.dy !== undefined) element.setAttribute("data-callout-from-dy", labelItem.tick.from.dy);
                if (labelItem.tick?.to?.dx !== undefined) element.setAttribute("data-callout-to-dx", labelItem.tick.to.dx);
                if (labelItem.tick?.to?.dy !== undefined) element.setAttribute("data-callout-to-dy", labelItem.tick.to.dy);
              } else {
                element.setAttribute("data-callout", "no");
              }
              textReplacer[cssName] = { "html": element.outerHTML, "name": "F_layer_" + i + "__markLabel_" + lii + "_" + field };
            });
            if ($sublayer_linemark_text !== undefined) {
              sublayer_linemark_text.push($sublayer_linemark_text)
            }
          }
        }
      }
    });

    if (isDebug) {// debugging annotation
      console.log(...lm("On-Mark annotations, after compiling all of them"));
      console.log(annotation);
    }

    // tooltip
    if (layer_info.tooltip !== undefined) {
      if (layer_info.tooltip.visibility === "fixed") view.fixTooltip = true;
      if (layer_info.tooltip.fields?.length > 0 && layer_info.tooltip.visibility !== "hidden") {
        if (["line", "area"].includes(layer_info.mark.type)) {
          mark.tooltip = true;
        } else {
          encoding.tooltip = layer_info.tooltip.fields.map(field => {
            if (field.constructor.name === "String") {
              return {
                "field": field,
                "type": datatype[field].type
              }
            } else if (field.constructor.name === "Object") {
              if (field.field === undefined) return undefined;
              let tooltipObj = {
                "field": field.field,
                "type": field.type || datatype[field.field]?.type
              };
              if (field.format) tooltipObj.format = field.format;
              if (field.expression !== undefined)
                tooltipDetail.expressions.push({
                  "field": field.field,
                  "role": layer_info.mark?.type,
                  "expression": field.expression
                })
              return tooltipObj;
            } else {
              return undefined;
            }
          }).filter(d => d !== undefined);
        }
      }
    }

    if (mark.type === "area" && layer_info.mark.clip != false) mark.clip = true;

    if (isDebug) {// debugging mark
      console.log(...lm("Mark information, right after compiling, before compiling projection"));
      console.log(mark);
    }
    if (isDebug) {// debugging encoding
      console.log(...lm("Encoding information, right after compiling, before compiling projection"));
      console.log(encoding);
    }

    // transforms & interactions
    let transforms = []; // transforms
    if (layer_info.transform?.length > 0) transforms = layer_info.transform;
    let layerObject = { name, data, mark, encoding };
    if (sublayer_linemark_text.length > 0) {
      layerObject.layer = [
        { mark: mark, "name": "F_layer_" + i + "__mark", }
      ]
      layerObject.layer.push(...sublayer_linemark_text);
      delete layerObject.mark;
    }
    if (params.length > 0) layerObject.params = params;
    if (transforms.length > 0) layerObject.transform = transforms;

    if (isDebug) {// debugging layerOBject
      console.log(...lm("Single Layer object, right before finishing the compiling of each layer"));
      console.log(layerObject);
    }

    return layerObject;
  });

  if (isDebug) {// debugging layers
    console.log(...lm("Entire Layer array, after compiling all of the layers"));
    console.log(layers);
  }

  // insert projection geoshape layer
  if (layout_type === "projection") {
    let geoshapeLayer = layers.filter(layer => layer.mark.type === "geoshape");
    if (geoshapeLayer.length > 0) { // choropleth-like
      geoshapeLayer.forEach((layer, index) => {
        layer.name = "F_geoLayer_" + index + (layer.name === undefined ? "__named_" + layer.name : "");
        if (layer.data === undefined) layer.data = {};
        if (layer.data !== undefined) layer.data.name = "geo_data_" + index;
        if (layer.data.format === undefined) layer.data.format = { "type": "topojson" };
        if (layer.data.format === undefined) layer.data.format = {};
        if (layer.data.format.feature === undefined || layer.data.format.url === undefined) {
          if (spec.layout.projection?.level === "us-state") {
            layer.data.format.feature = "states";
            layer.data.url = base_url + "data/us-10m.json";
          } else if (spec.layout.projection?.level === "us-county") {
            layer.data.format.feature = "counties";
            layer.data.url = base_url + "data/us-10m.json";
          } else if (spec.layout.projection?.url !== undefined) {
            layer.data.url = spec.layout.projection.url;
            if (spec.layout.projection?.feature) {
              layer.data.format.feature = spec.layout.projection.feature;
            }
          } else { // default: country level
            layer.data.format.feature = "countries";
            layer.data.url = base_url + "data/world-110m.json";
          }
        }
        // custom map
        if (spec.layout.projection?.customMap) {
          layer.data.url = base_url + "data/" + spec.layout.projection.customMap;
        }
        if (layer.mark?.feature !== undefined) {
          layer.data.format.feature = layer.mark.feature;
        }
        delete layer.encoding.longitude;
        delete layer.encoding.latitude;
        if (spec.layout.projection?.datakey !== undefined) {
          let lookupFields = Object.keys(spec.datatype);
          let nameKeyIndex = lookupFields.indexOf(spec.layout.projection?.datakey);
          if (nameKeyIndex >= 0) {
            lookupFields.splice(nameKeyIndex, 1);
          }
          lookupFields = Object.keys(layer.encoding).map(chan => (layer.encoding[chan].field || null)).filter(d => d !== null);
          let lookup = {
            "lookup": spec.layout.projection?.linkkey || "id",
            "from": {
              "data": { "name": "values" },
              "key": spec.layout.projection?.datakey,
              "fields": lookupFields
            }
          };
          if (layer.transform === undefined) layer.transform = [];
          layer.transform.push(lookup);
        }
        // get projection style
        if (layer.mark.fill === undefined && layer.mark.color === undefined && layer.encoding.color === undefined && layer.encoding.fill === undefined) layer.mark.fill = spec.layout?.projection?.style?.fill || "white";
        if (layer.mark.opacity === undefined && layer.encoding.opacity === undefined) layer.mark.opacity = spec.layout?.projection?.style?.opacity || 1;
        if (layer.mark.stroke === undefined && layer.encoding.stroke === undefined) layer.mark.stroke = spec.layout.projection.style?.stroke || "lightgray";
        if (layer.mark.strokeOpacity === undefined && layer.encoding.strokeOpacity === undefined) layer.mark.strokeOpacity = spec.layout?.projection?.style?.strokeOpacity || 1;
        if (layer.mark.strokeWidth === undefined && layer.encoding.strokeWidth === undefined) layer.mark.strokeWidth = spec.layout?.projection?.style?.strokeWidth || 1;
      });
    } else {
      let name = "F_geoLayer_0"
      let data = {
        "name": "geo_data",
        "format": { "type": "topojson" }
      }
      let mark = {
        "type": "geoshape"
      }
      // get projection data 
      if (spec.layout.projection?.level === "us-state") {
        data.format.feature = "states";
        data.url = base_url + "data/us-10m.json";
      } else if (spec.layout.projection?.level === "us-county") {
        data.format.feature = "counties";
        data.url = base_url + "data/us-10m.json";
      } else if (spec.layout.projection?.url !== undefined) {
        data.url = spec.layout.projection.url;
        if (spec.layout.projection?.feature) {
          data.format.feature = spec.layout.projection.feature;
        }
      } else { // default: country level
        data.format.feature = "countries";
        data.url = base_url + "data/world-110m.json";
      }
      // custom map
      if (spec.layout.projection?.customMap) {
        data.url = base_url + "data/" + spec.layout.projection.customMap;
      }
      // get projection style
      mark.fill = (spec.layout?.projection?.style?.fill !== undefined ? spec.layout.projection.style.fill : "white");
      mark.opacity = (spec.layout?.projection?.style?.opacity !== undefined ? spec.layout.projection.style.opacity : 1);
      mark.stroke = (spec.layout?.projection?.style?.stroke !== undefined ? spec.layout.projection.style.stroke : "lightgray");
      mark.strokeOpacity = (spec.layout?.projection?.style?.strokeOpacity !== undefined ? spec.layout.projection.style.strokeOpacity : 1);
      mark.strokeWidth = (spec.layout?.projection?.style?.strokeWidth !== undefined ? spec.layout.projection.style.strokeWidth : 1);
      layers.splice(0, 0, { name, data, mark }); // insertion
    }
  }

  // 2.c Format interaction (context view will be fixed later; tooltip & interactive filtering are already fixed)
  // zoom
  let hasMapZoom = false;
  if (layout_type === "projection" && spec.interaction?.zoompan === true) {
    hasMapZoom = true;
  } else if (spec.interaction?.zoompan === true) {
    if (layers[0].params === undefined) layers[0].params = [];
    layers[0].params.push({
      "name": "__zoom_pan__",
      "select": "interval",
      "bind": "scales"
    });
  }

  // post processing
  if (layout_type === "faceted") { // faceted views (done)
    let splits = spec.layout?.split;
    if (splits?.filter !== undefined) { // split views (done)
      let curr_layer = layers[0]; // get the first layer (only considers the first layer, as layering is prohibited)
      let facets = Fronto.range(n_facets).map(i => { // for each facet
        let facet = Fronto.deepcopy(curr_layer);
        facet.name = facet.name + "__facet_" + i;
        if (facet.trasform === undefined) facet.transform = [];
        facet.transform.push({ "filter": splits.filter[i] });
      });
      view.columns = facet_layout.n_columns

      view.concat = facets; // use concat for flexible layout
    } else if (splits?.field !== undefined) { // small multiples (done)
      let curr_layer = layers[0] || { encoding: {} };
      curr_layer.encoding.facet = { // add a facet encoding
        "field": splits.field,
        "columns": facet_layout.n_columns,
        "type": "nominal", // default
      }
      // text setting (todo)
      curr_layer.encoding.facet.header = {};
      curr_layer.encoding.facet.header.title = null; // default
      curr_layer.encoding.facet.header.labels = false;

      //copy
      view.data = curr_layer.data;
      view.mark = curr_layer.mark;
      view.encoding = curr_layer.encoding || {};
      if (curr_layer.params) {
        if (view.params === undefined) view.params = [];
        view.params = view.params.concat(curr_layer.params)
      }
      if (curr_layer.transform) {
        if (view.transform === undefined) view.transform = [];
        view.transform = view.transform.concat(curr_layer.transform)
      }
      if (splits?.values?.length > 0) {
        let split_filter_statement = "includes(" + JSON.stringify(splits.values) + ", datum." + splits.field + ")";
        view.encoding.facet.sort = Fronto.deepcopy(splits.values);
        if (view.transform === undefined) view.transform = [];
        view.transform.push({ // add a filter for the specified values
          "filter": split_filter_statement
        });
      }
    } else { // split is not defined
      let curr_layer = layers[0];
      view.data = curr_layer.data;
      view.mark = curr_layer.mark;
      view.encoding = curr_layer.encoding;
      if (curr_layer.params) {
        if (view.params === undefined) view.params = [];
        view.params = view.params.concat(curr_layer.params)
      }
      if (curr_layer.transform) {
        if (view.transform === undefined) view.transform = [];
        view.transform = view.transform.concat(curr_layer.transform)
      }
    }
    if (splits?.filter !== undefined || splits?.field !== undefined) {
      if (splits?.label !== undefined && splits?.label !== false) {
        delete view.encoding.facet.header.labels;
        if (splits.label.fontSize !== undefined) view.encoding.facet.header.labelFontSize = splits.label.fontSize;
        if (splits.label.fontWeight !== undefined) view.encoding.facet.header.labelFontWeight = splits.label.fontWeight;
        if (splits.label.anchor !== undefined) view.encoding.facet.header.labelAnchor = splits.label.anchor;
        if (splits.label.align !== undefined) view.encoding.facet.header.labelAlign = splits.label.align;
        if (splits.label.color !== undefined) view.encoding.facet.header.labelColor = splits.label.color;
        if (splits.label.offset !== undefined) view.encoding.facet.header.labelPadding = splits.label.offset;
        // todo% support expresssion!!!!!!!!!
        let labelItem = splits.label;
        if (labelItem.items?.length > 0) {
          let maxWidth = d3.max(labelItem.items.map(h => h.width));
          if (maxWidth !== undefined) view.encoding.facet.header.labelLimit = maxWidth * 1.1;
          let fillerWidth = maxWidth;
          let textValueItems = splits.values || datatype[splits.field].values;
          if (fillerWidth === undefined) {
            fillerWidth = d3.max(textValueItems.map(h => h.toString().length)) * 3;
          }
          let exprFiller = "!" + "@".repeat(Math.max(Math.round((fillerWidth - 20) / 5 - 3), 0))
          let axisInitial = "m";
          if (datatype[splits.field].type === "temporal") {
            view.encoding.facet.header.labelExpr = "'$-" + axisInitial + "-' + roundN(datum.index*10000,0) + '" + exprFiller + "'";
            view.encoding.facet.header.labelClass = "'$-" + axisInitial + "-' + roundN(datum.index*10000,0) + ' ' + datum.value + ' ' + datum.index";
          } else {
            view.encoding.facet.header.labelExpr = "'$-" + axisInitial + "-' + hashString(toStringD(datum.value)) + '" + exprFiller + "'";
            view.encoding.facet.header.labelClass = "'$-" + axisInitial + "-' + hashString(toStringD(datum.value)) + ' ' + datum.value + ' ' + datum.index";
          }

          // register replacement!! ==> textReplacer
          function axisReplacementCreator(valueItem, vindex) {
            let cssName;
            let axisInitial = "m";
            if (datatype[splits.field].type === "temporal") {
              cssName = "$-" + axisInitial + "-" + Fronto.round(vindex / (textValueItems.length - 1) * 10000, 0);
            } else {
              cssName = "$-" + axisInitial + "-" + Fronto.toStringD(valueItem).hashString();
            }
            let element = document.createElement("div");
            if (labelItem.angle !== undefined) element.style.transform += ' rotate(' + labelItem.angle + ')';
            if (labelItem.align !== undefined) element.style.textAlign = labelItem.align;
            element.className = cssName;
            if (labelItem.dx !== undefined) { element.style.transform += ' translateX(' + labelItem.dx + ')' }
            if (labelItem.dy !== undefined) { element.style.transform += ' translateY(' + labelItem.dy + ')' }
            element.setAttribute("data-orient", "top");
            if (labelItem.flex !== undefined && labelItem.flex !== "vertical") {
              if (labelItem.flex === "horizontal-space-between") {
                element.style.display = "flex";
                element.style.justifyContent = "space-between";
              } else if (labelItem.flex === "horizontal-start") {
                element.style.display = "flex";
                element.style.justifyContent = "flex-start";
              } else if (labelItem.flex === "horizontal-end") {
                element.style.display = "flex";
                element.style.justifyContent = "flex-end";
              } else if (labelItem.flex === "horizontal-center") {
                element.style.display = "flex";
                element.style.justifyContent = "center";
              } else if (labelItem.flex === "horizontal") {
                element.style.display = "flex";
              }
            }
            labelItem.items.forEach(textItem => {
              if (textItem.values !== undefined) {
                if (!textItem.values.includes(valueItem)) return;
              }
              let condEval;
              if (textItem.condition !== undefined) {
                if (textItem.condition.constructor.name === "String") {
                  let evalExpr = textItem.condition.replace(/(datum\.value)/g, "'" + valueItem + "'");
                  evalExpr = evalExpr.replace(/(datum\.index)/g, vindex / (textValueItems.length - 1));
                  let currDatum = dataset.filter(ditem => ditem[splits.field] === valueItem || ditem[splits.field] == valueItem)[0]
                  if (currDatum === undefined) currDatum = {};
                  evalExpr = evalExpr.replace(/(datum)/g, JSON.stringify(currDatum))
                  if (!evalExpr.startsWith('(')) evalExpr = "(" + evalExpr + ")";
                  condEval = Fronto.evalExpression(evalExpr);
                } else if (textItem.condition.constructor.name === "Array") {
                  condEval = textItem.condition.includes(valueItem);
                }
              }
              if (condEval === undefined || condEval === true) {
                let textContent;
                if (textItem.expression !== undefined) {
                  let datum = spec.data.filter((d) => d[splits.field] === valueItem)[0];
                  let evalExpr = textItem.expression.replace(/datum\.value/g, (typeof valueItem == "string" ? "'" + valueItem + "'" : valueItem));
                  evalExpr = evalExpr.replace(/(datum\.index)/g, vindex / (textValueItems.length - 1)).replace(/(datum)/g, JSON.stringify(datum));
                  textContent = Fronto.evalExpression(evalExpr, splits.field, valueItem, spec.data, true);
                } else if (textItem.custom !== undefined) {
                  textContent = textItem.custom[valueItem] ? textItem.custom[valueItem] : (textItem.format ? Fronto.evalFormat(textItem.format, valueItem) : valueItem);
                } else if (textItem.format !== undefined) {
                  let valueItem2 = (labesplitslItem.field === "year" && datatype[splits.field].type === "nominal" ? valueItem + "-02-01" : valueItem);
                  textContent = Fronto.evalFormat(textItem.format, valueItem2, textItem.formatType || datatype[encoding[axis].field].type)
                } else if (textItem.value !== undefined) {
                  textContent = textItem.value;
                } else {
                  textContent = valueItem;
                }
                let textItemElement = document.createElement('div');
                textItemElement.innerHTML = textContent;
                if (textItem.width !== undefined) textItemElement.style.width = textItem.width + 'px';
                else if (labelItem.width !== undefined) textItemElement.style.width = labelItem.width + 'px';
                if (textItem.baseline !== undefined) textItemElement.style.verticalAlign = textItem.baseline;
                if (textItem.color !== undefined) textItemElement.style.color = textItem.color;
                if (textItem.fontSize !== undefined) textItemElement.style.fontSize = textItem.fontSize + 'px';
                if (textItem.fontWeight !== undefined) textItemElement.style.fontWeight = textItem.fontWeight;
                if (textItem.opacity !== undefined) textItemElement.style.opacity = textItem.opacity;
                if (textItem.fill !== undefined) textItemElement.style.backgroundColor = textItem.fill;
                if (textItem.align !== undefined) textItemElement.style.textAlign = textItem.align;
                else if (element.style.textAlign !== undefined) textItemElement.style.textAlign = element.style.textAlign;
                else textItemElement.style.textAlign = "left";
                if (textItem.orient !== undefined) element.setAttribute("data-orient", textItem.orient);
                element.appendChild(textItemElement);
              }
            });
            textReplacer[cssName] = { "html": element.outerHTML, "name": "F_multiples_headerLabel_" + splits.field };
          }
          textValueItems.forEach((valueItem, vindex) => axisReplacementCreator(valueItem, vindex))

          let fixedValue = splits.values !== undefined;
          if (!fixedValue) {
            let axisInitial = "m";
            let cssName2 = "$-" + axisInitial + "-blank";
            textReplacer[cssName2] = { "creator": axisReplacementCreator };
          }
        }

      } else {
        view.encoding.facet.header.title = null;
        view.encoding.facet.header.labels = false;
        view.encoding.facet.header.labelExpr = "''";
      }
    }

    if (spec.layout?.haxis?.repeat == true) {
      if (view.resolve === undefined) view.resolve = {};
      if (view.resolve.axis === undefined) view.resolve.axis = {};
      view.resolve.axis.x = "independent";
    }
    if (spec.layout?.vaxis?.repeat == true) {
      if (view.resolve === undefined) view.resolve = {};
      if (view.resolve.axis === undefined) view.resolve.axis = {};
      view.resolve.axis.y = "independent";
    }

  } else if (layout_type === "context") { // context views (done)
    let curr_layer = layers[0];
    curr_layer.view = { "stroke": null };
    let context_layer = Fronto.deepcopy(curr_layer); // copy
    context_layer.name = context_layer.name + "__context"
    context_layer.view = { "stroke": null };
    // sizing (main)
    if (view.width) curr_layer.width = view.width;
    if (view.height) curr_layer.height = view.height;
    delete view.height;
    // sizing (context)
    if (view.width) context_layer.width = view.width;
    delete view.width;
    if (spec.interaction?.context?.height) context_layer.height = spec.interaction.context.height;
    else context_layer.height = 100;
    // parameter setting
    curr_layer.encoding = Fronto.setObjectValueCascaded(curr_layer.encoding, ["x", "scale", "domain", "param"], "__brush__");
    if (context_layer.params === undefined) context_layer.params = []; // add params to the context facet
    context_layer.params.push({ "name": "__brush__", "select": { "type": "interval", "encodings": ["x"] } });
    // insertion
    view.vconcat = [curr_layer, context_layer];
  } else if (layout_type === "tabular") {
    let curr_layer = layers[0];
    if (curr_layer) {
      view.data = curr_layer.data;
      view.mark = curr_layer.mark;
      view.encoding = curr_layer.encoding;
      if (curr_layer.params) {
        if (view.params === undefined) view.params = [];
        view.params = view.params.concat(curr_layer.params)
      }
      if (curr_layer.transform) {
        if (view.transform === undefined) view.transform = [];
        view.transform = view.transform.concat(curr_layer.transform)
      }
    }
    if (view.config === undefined) view.config = {};
    view.config.facet = { spacing: 5 };
    if (!view.layer && view.encoding?.row) {
      view.config.facet = { spacing: 15 };
    }

    if (spec.layout?.haxis?.repeat == true) {
      if (view.resolve === undefined) view.resolve = {};
      if (view.resolve.axis === undefined) view.resolve.axis = {};
      view.resolve.axis.x = "independent";
    }
    if (spec.layout?.vaxis?.repeat == true) {
      if (view.resolve === undefined) view.resolve = {};
      if (view.resolve.axis === undefined) view.resolve.axis = {};
      view.resolve.axis.y = "independent";
    }
  } else { // else
    view.layer = layers;
  }

  // global transform => layer-wise transform;
  if (view.layer !== undefined && view.transform !== undefined) {
    view.layer.forEach(layer => {
      if (!layer.name.startsWith("F_geoLayer_")) {
        let t = Fronto.deepcopy(view.transform);
        if (layer.transform !== undefined) {
          layer.transform.push(...t);
        } else layer.transform = t;
      }
    })
    delete view.transform;
  }

  // 3. Extract annotation and emphasis
  if (spec.annotation?.items?.length > 0) {
    spec.annotation.items.forEach((anno, ni) => {
      let annotationItem = Fronto.deepcopy(anno);
      if (annotationItem.text !== undefined
        && (annotationItem.visibility?.hidden === false
          || (annotationItem.visibility?.hidden === undefined && spec.annotation.visibility?.hidden !== true))) {
        let annoItem = {};
        annoItem.name = annotationItem.name || "annotation_text_" + ni;
        annoItem.className = "F_annotation_" + ni;
        let dataAnchor = annotationItem.anchor?.dataItem !== undefined;
        // positioning
        if (!dataAnchor && annotationItem.x !== undefined) annoItem.x = annotationItem.x;
        if (!dataAnchor && annotationItem.y !== undefined) annoItem.y = annotationItem.y;
        if (annotationItem.dx !== undefined) annoItem.dx = annotationItem.dx;
        if (annotationItem.dy !== undefined) annoItem.dy = annotationItem.dy;
        if (annotationItem.width !== undefined) annoItem.width = annotationItem.width;
        if (annotationItem.height !== undefined) annoItem.height = annotationItem.height;
        if (annotationItem.autoPositioning == true) annoItem.autoPositioning = true;
        if (annotationItem.rotate !== undefined) annoItem.rotate = annotationItem.rotate;

        // data link
        if (dataAnchor) {
          annoItem.anchor = annotationItem.anchor;
          annoItem.anchor.textAnchor = true;
          if (annoItem.anchor.positionOrient === undefined) annoItem.anchor.positionOrient = "middle-center";
          if (annoItem.anchor.anchorAlign === undefined) annoItem.anchor.anchorAlign = "start";
          if (annoItem.anchor.callout) annoItem.anchor.callout = "yes";
          else annoItem.anchor.callout = "no";
          if (annoItem.anchor.from === undefined) annoItem.anchor.from = { dx: 0, dy: 0 };
          else {
            if (annoItem.anchor.from?.dx === undefined) annoItem.anchor.from.dx = 0;
            else if (annoItem.anchor.from?.dy === undefined) annoItem.anchor.from.dy = 0;
          }
          if (annoItem.anchor.to === undefined) annoItem.anchor.to = { dx: annoItem.dx, dy: annoItem.dy };
          else {
            if (annoItem.anchor?.to?.dx === undefined) annoItem.anchor.to.dx = annoItem.dx;
            else if (annoItem.anchor?.to?.dy === undefined) annoItem.anchor.to.dy = annoItem.dy;
          }
        } else if (annotationItem.anchor?.positionOrient) {
          annoItem.positionOrient = annotationItem.anchor?.positionOrient || "top-left";
        }

        // text element
        annoItem.text = {};
        if (annotationItem.text?.constructor.name === "Object") {
          annoItem.text.value = annotationItem.text.text;
          // text alignment
          if (annotationItem.text.align !== undefined) annoItem.text.align = annotationItem.text.align;
          else if (spec.annotation.textStyle?.align !== undefined) annoItem.text.align = spec.annotation.textStyle.align;
          else annoItem.text.align = "center";
          // font size
          if (annotationItem.text.fontSize !== undefined) annoItem.text.size = annotationItem.text.fontSize;
          else if (spec.annotation.textStyle?.fontSize !== undefined) annoItem.text.size = spec.annotation.textStyle.fontSize;
          else annoItem.text.size = 14;
          // font weight
          if (annotationItem.text.fontWeight !== undefined) annoItem.text.weight = annotationItem.text.fontWeight;
          else if (spec.annotation.textStyle?.fontWeight !== undefined) annoItem.text.weight = spec.annotation.textStyle.fontWeight;
          else annoItem.text.weight = 400;
          // line height
          if (annotationItem.text.lineHeight !== undefined) annoItem.text.lineHeight = annotationItem.text.lineHeight;
          else if (spec.annotation.textStyle?.lineHeight !== undefined) annoItem.text.lineHeight = spec.annotation.textStyle.lineHeight;
          else annoItem.text.lineHeight = 160;
          // font color
          if (annotationItem.text.fontColor !== undefined) annoItem.text.color = annotationItem.text.fontColor;
          else if (spec.annotation.textStyle?.fontColor !== undefined) annoItem.text.color = spec.annotation.textStyle.fontColor;
          // opacity
          if (annotationItem.text.opacity !== undefined) annoItem.text.opacity = annotationItem.text.opacity;
          else if (spec.annotation.textStyle?.opacity !== undefined) annoItem.text.opacity = spec.annotation.textStyle.opacity;
          // overflow
          if (annotationItem.text.overflow !== undefined) annoItem.text.overflow = annotationItem.text.overflow;
          else if (spec.annotation.textStyle?.overflow !== undefined) annoItem.text.overflow = spec.annotation.textStyle.overflow;
        } else if (annotationItem.text?.constructor.name === "Array") {
          // just compile the entire html
          annoItem.text.value = "";
          annotationItem.text.forEach(textSubItem => {
            let textHTML = document.createElement("div");
            textHTML.innerHTML = textSubItem.text;
            // text alignment
            if (textSubItem.align !== undefined) textHTML.style.textAlign = textSubItem.align;
            else if (spec.annotation.textStyle?.align !== undefined) textHTML.style.textAlign = spec.annotation.textStyle.align;
            else textHTML.style.textAlign = "center";
            // font size
            if (textSubItem.fontSize !== undefined) textHTML.style.fontSize = Fronto.unitString(textSubItem.fontSize, "px");
            else if (spec.annotation.textStyle?.fontSize !== undefined) textHTML.style.fontSize = Fronto.unitString(spec.annotation.textStyle.fontSize, "px");
            else textHTML.style.fontSize = 14;
            // font weight
            if (textSubItem.fontWeight !== undefined) textHTML.style.fontWeight = textSubItem.fontWeight;
            else if (spec.annotation.textStyle?.fontWeight !== undefined) textHTML.style.fontWeight = spec.annotation.textStyle.fontWeight;
            else textHTML.style.fontWeight = 400;
            // line height
            if (textSubItem.lineHeight !== undefined) textHTML.style.lineHeight = Fronto.unitString(textSubItem.lineHeight, "%");
            else if (spec.annotation.textStyle?.lineHeight !== undefined) textHTML.style.lineHeight = Fronto.unitString(spec.annotation.textStyle.lineHeight, "%");
            else textHTML.style.lineHeight = "160%";
            // font color
            if (textSubItem.fontColor !== undefined) textHTML.style.color = textSubItem.fontColor;
            else if (spec.annotation.textStyle?.fontColor !== undefined) textHTML.style.color = spec.annotation.textStyle.fontColor;
            // opacity
            if (textSubItem.opacity !== undefined) textHTML.style.opacity = textSubItem.opacity;
            else if (spec.annotation.textStyle?.opacity !== undefined) textHTML.style.opacity = spec.annotation.textStyle.opacity;
            // overflow
            if (textSubItem.overflow !== undefined) textHTML.style.textOverflow = textSubItem.overflow;
            else if (spec.annotation.textStyle?.overflow !== undefined) textHTML.style.textOverflow = spec.annotation.textStyle.overflow;
            // assign
            annoItem.text.value = annoItem.text.value + textHTML.outerHTML;
          });
        }

        // box styling
        if (annotationItem.box !== undefined) {
          annoItem.box = {};
          // padding
          if (annotationItem.box.paddingTop !== undefined) annoItem.box.paddingTop = annotationItem.box.paddingTop;
          else if (spec.annotation.boxStyle?.paddingTop !== undefined) annoItem.box.paddingTop = spec.annotation.boxStyle.box.paddingTop;
          if (annotationItem.box.paddingBottom !== undefined) annoItem.box.paddingBottom = annotationItem.box.paddingBottom;
          else if (spec.annotation.boxStyle?.paddingBottom !== undefined) annoItem.box.paddingBottom = spec.annotation.boxStyle.box.paddingBottom;
          if (annotationItem.box.paddingLeft !== undefined) annoItem.box.paddingLeft = annotationItem.box.paddingLeft;
          else if (spec.annotation.boxStyle?.paddingLeft !== undefined) annoItem.box.paddingLeft = spec.annotation.boxStyle.box.paddingLeft;
          if (annotationItem.box.paddingRight !== undefined) annoItem.box.paddingRight = annotationItem.box.paddingRight;
          else if (spec.annotation.boxStyle?.paddingRight !== undefined) annoItem.box.paddingRight = spec.annotation.boxStyle.box.paddingRight;
          if (annotationItem.box.padding !== undefined) {
            if (annotationItem.box.padding.constructor.name === "Number") annoItem.box.padding = annotationItem.box.padding;
            else if (annotationItem.box.padding.constructor.name === "String") {
              let paddingArray = annotationItem.box.padding.split(" ");
              if (paddingArray.length == 1) {
                annoItem.box.padding = annotationItem.box.padding;
              } else if (paddingArray.length == 2) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[0];
                annoItem.box.paddingLeft = paddingArray[1];
                annoItem.box.paddingRight = paddingArray[1];
              } else if (paddingArray.length == 3) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[2];
                annoItem.box.paddingLeft = paddingArray[1];
                annoItem.box.paddingRight = paddingArray[1];
              } else if (paddingArray.length == 4) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[2];
                annoItem.box.paddingLeft = paddingArray[3];
                annoItem.box.paddingRight = paddingArray[1];
              }
            }
          }
          else if (spec.annotation.boxStyle?.padding !== undefined) {
            if (spec.annotation.boxStyle.padding.constructor.name === "Number") annoItem.box.padding = spec.annotation.boxStyle.padding;
            else if (spec.annotation.boxStyle.padding.constructor.name === "String") {
              let paddingArray = spec.annotation.boxStyle.padding.split(" ");
              if (paddingArray.length == 1) {
                annoItem.box.padding = spec.annotation.boxStyle.padding;
              } else if (paddingArray.length == 2) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[0];
                annoItem.box.paddingLeft = paddingArray[1];
                annoItem.box.paddingRight = paddingArray[1];
              } else if (paddingArray.length == 3) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[2];
                annoItem.box.paddingLeft = paddingArray[1];
                annoItem.box.paddingRight = paddingArray[1];
              } else if (paddingArray.length == 4) {
                annoItem.box.paddingTop = paddingArray[0];
                annoItem.box.paddingBottom = paddingArray[2];
                annoItem.box.paddingLeft = paddingArray[3];
                annoItem.box.paddingRight = paddingArray[1];
              }
            }
          }
          if (annotationItem.box.radius !== undefined) annoItem.box.rounded = annotationItem.box.radius;
          else if (spec.annotation.boxStyle?.radius !== undefined) annoItem.box.rounded = spec.annotation.boxStyle.box.radius;
          if (annotationItem.box.fill !== undefined) annoItem.box.fill = annotationItem.box.fill;
          else if (spec.annotation.boxStyle?.fill !== undefined) annoItem.box.fill = spec.annotation.boxStyle.box.fill;
          if (annotationItem.box.opacity !== undefined) annoItem.box.opacity = annotationItem.box.opacity;
          else if (spec.annotation.boxStyle?.opacity !== undefined) annoItem.box.opacity = spec.annotation.boxStyle.box.opacity;
          if (annotationItem.box.stroke !== undefined) annoItem.box.borderColor = annotationItem.box.stroke;
          else if (spec.annotation.boxStyle?.stroke !== undefined) annoItem.box.borderColor = spec.annotation.boxStyle.box.stroke;
          if (annotationItem.box.strokeWidth !== undefined) annoItem.box.borderWidth = annotationItem.box.strokeWidth;
          else if (spec.annotation.boxStyle?.strokeWidth !== undefined) annoItem.box.borderWidth = spec.annotation.boxStyle.box.strokeWidth;
          if (annotationItem.box.strokeStyle !== undefined) annoItem.box.borderStyle = annotationItem.box.strokeStyle;
          else if (spec.annotation.boxStyle?.strokeStyle !== undefined) annoItem.box.borderStyle = spec.annotation.boxStyle.box.strokeStyle;
          if (annotationItem.box.strokeOpacity !== undefined) annoItem.box.borderOpacity = annotationItem.box.strokeOpacity;
          else if (spec.annotation.boxStyle?.strokeOpacity !== undefined) annoItem.box.borderOpacity = spec.annotation.boxStyle.box.strokeOpacity;
        }
        // visibility setup
        if (annotationItem.visibility?.externalize !== undefined || spec.annotation.visibility?.externalize !== undefined) {
          annoItem.externalize = { "type": "yes" };
          if (annotationItem.visibility?.externalize.position !== undefined) annoItem.externalize.position = annotationItem.visibility.externalize.position;
          else if (spec.annotation.visibility?.externalize.position !== undefined) annoItem.externalize.position = spec.annotation.visibility.externalize.position;
          if (annotationItem.visibility?.externalize.numbering === true) annoItem.externalize.numbering = "yes";
          else if (spec.annotation.visibility?.externalize.numbering === true) annoItem.externalize.numbering = "yes";
        }
        if (dataAnchor) {
          annotation.data.items.push(annoItem);
        } else {
          annotation.nondata.items.push(annoItem);
        }
      }
    })
  }

  if (spec.emphasis?.items?.length > 0) {
    spec.emphasis.items.forEach((nondataItem, ni) => {
      if (nondataItem.mark !== undefined
        && (nondataItem.visibility?.hidden === false
          || (nondataItem.visibility?.hidden === undefined && spec.nondata.visibility?.hidden !== true))) {
        emphasis.items.push(Fronto.deepcopy(nondataItem));
      }
    })
  }

  if (isDebug) {// debugging annotation
    console.log(...lm("Multiple text items for non-data annotations, after creation"));
    console.log(annotation, emphasis);
  }

  // 4. Extract titles
  let title = [];
  if (spec.title?.items?.length > 0) {
    spec.title.items.forEach((titleItem, ti) => {
      let titleContent = {
        "className": "F_title_" + ti,
        "text": {},
        "box": {}
      };
      if (titleItem.name !== undefined) titleContent.name = titleItem.name;
      // text
      if (titleItem.text !== undefined) titleContent.text.value = titleItem.text;
      if (titleItem.fontSize !== undefined) titleContent.text.size = titleItem.fontSize;
      if (titleItem.fontWeight !== undefined) titleContent.text.weight = titleItem.fontWeight;
      if (titleItem.lineHeight !== undefined) titleContent.text.lineHeight = titleItem.lineHeight;
      if (titleItem.fontColor !== undefined) titleContent.text.color = titleItem.fontColor;
      if (titleItem.opacity !== undefined) titleContent.text.opacity = titleItem.opacity;
      if (titleItem.align !== undefined) titleContent.text.align = titleItem.align;
      // container (box)
      if (titleItem.offset !== undefined) titleContent.box.marginBottom = titleItem.offset;
      if (titleItem.width !== undefined) titleContent.box.width = titleItem.width;
      else if (spec.title.width !== undefined) titleContent.box.width = spec.title.width;
      else if (spec.layout.width !== undefined) {
        titleContent.box.width = spec.layout.width;
      }
      if (titleItem.orient !== undefined) titleContent.box.align = titleItem.orient;
      else if (spec.title.orient !== undefined) titleContent.box.align = spec.title.orient;
      else if (titleItem.align !== undefined) titleContent.box.align = titleItem.align;
      else if (spec.title.align !== undefined) titleContent.box.align = spec.title.align;
      title.push(titleContent)
    })
  }

  // multiples ==> axis label resolve
  if (layout_type === "faceted") {
    if (view.encoding?.x?.axis !== null || view.encoding?.x?.axis !== false) {
      if (!view.resolve) view.resolve = {};
      if (!view.resolve.axis) view.resolve.axis = {};
      view.resolve.axis.x = "independent";
    }
    if (view.encoding?.y?.axis !== null || view.encoding?.y?.axis !== false) {
      if (!view.resolve) view.resolve = {};
      if (!view.resolve.axis) view.resolve.axis = {};
      view.resolve.axis.y = "independent";
    }
  }


  let vlView = Fronto.deepcopy(view);
  // 5. interaction adjustment;
  // in case of map has zoom + pan => convert to vega
  if (hasMapZoom) {
    // get vega spec compiled
    let vegaView = vegaLite.compile(view, {}).spec;
    vegaView.name = vlView.name;
    if (view.fixTooltip !== undefined) vegaView.fixTooltip = view.fixTooltip;
    // sanity check for "projection"
    if (vegaView.projections === undefined) vegaView.projections = [];
    if (vegaView.projections.length == 0) vegaView.projections.push({});
    // set singals for zoom/pan interaction
    vegaView.projections[0].scale = { "signal": "scale" };
    vegaView.projections[0].rotate = [{ "signal": "rotateX" }, 0, 0];
    vegaView.projections[0].center = [0, { "signal": "centerY" }];
    vegaView.projections[0].translate = [{ "signal": "tx" }, { "signal": "ty" }];

    // add singals
    let projectionName = (vegaView.projections[0]?.name ? vegaView.projections[0].name : "projection");
    let geoLayer = view.layer.filter(layer => layer.mark.type === "geoshape")[0];
    let geoFeature;
    if (geoLayer) geoFeature = geoLayer.data?.format?.feature;
    if (!geoFeature) geoFeature = "countries";
    const defaultMapScale = (geoFeature === "counties" || geoFeature === "states" ? 1000 : 150);
    const projectionZoomPanSignals = [
      { "name": "tx", "update": "width / 2" },
      { "name": "ty", "update": "height / 2" },
      {
        "name": "scale", "value": (view.projection?.scale ? view.projection.scale : defaultMapScale),
        "on": [{
          "events": { "type": "wheel", "consume": true },
          "update": "clamp(scale * pow(1.0005, -event.deltaY * pow(16, event.deltaMode)), " + (view.projection?.scale ? view.projection.scale : defaultMapScale) + " , 8000)"
        }]
      },
      { "name": "angles", "value": [0, 0], "on": [{ "events": "mousedown", "update": "[rotateX, centerY]" }] },
      { "name": "cloned", "value": null, "on": [{ "events": "mousedown", "update": "copy('" + projectionName + "')" }] },
      { "name": "start", "value": null, "on": [{ "events": "mousedown", "update": "invert(cloned, xy())" }] },
      { "name": "drag", "value": null, "on": [{ "events": "[mousedown, window:mouseup] > window:mousemove", "update": "invert(cloned, xy())" }] },
      { "name": "delta", "value": null, "on": [{ "events": { "signal": "drag" }, "update": "[drag[0] - start[0], start[1] - drag[1]]" }] },
      { "name": "rotateX", "value": 0, "on": [{ "events": { "signal": "delta" }, "update": "angles[0] + delta[0]" }] },
      { "name": "centerY", "value": 0, "on": [{ "events": { "signal": "delta" }, "update": "clamp(angles[1] + delta[1], -60, 60)" }] }
    ];
    delete vegaView.projections.size;
    if (vegaView.signals === undefined) vegaView.signals = [];
    projectionZoomPanSignals.forEach(signal => {
      vegaView.signals.push(signal);
    })
    // add transformations  to non-geo layers
    vegaView.marks.forEach(markLayer => {
      if (!markLayer.style.includes("geoshape")) {
        if (markLayer.transform === undefined) markLayer.transform = [];
        let nameSplit = markLayer.name.split("_");
        let prefix = nameSplit.slice(0, nameSplit.length - 1).join("_");
        markLayer.transform.push({
          "type": "geojson",
          "fields": [prefix + "_x", prefix + "_y"]
        });
        markLayer.encode.update.x = { "field": prefix + "_x" };
        markLayer.encode.update.y = { "field": prefix + "_y" };
      }
    })
    view = vegaView;
  }

  if (isDebug) {// debugging textReplacer
    console.log(...lm("Text Replacer items, after creation, right before returning it from the compiler"));
    console.log(textReplacer);
  }

  if (isDebug) {// debugging view
    console.log(...lm("Final View object, after creation, right before returning it from the compiler"));
    console.log(view);
  }

  return { view, vlView, annotation, emphasis, title, textReplacer, tooltipDetail };
}
